import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom'
import ProviderLogo from '../components/ProviderLogo'
import { motion, Reorder } from 'framer-motion'
import {
  ArrowLeft,
  Calendar,
  Check,
  Clock,
  Users,
  Play,
  Edit2,
  ExternalLink,
  FileText,
  CheckCircle,
  AlertCircle,
  Share2,
  Volume2,
  SkipForward,
  Square,
  RefreshCw,
  Bot,
  Loader2,
  Radio,
  Repeat,
  ChevronDown,
  ChevronRight,
  GripVertical,
  ListTodo,
  Target,
  Lightbulb,
  Ban,
  Trash2,
  ClipboardCheck,
  Mail,
  Copy,
  Download,
  Save,
  Send,
  Plus,
  Mic,
  MicOff,
  Undo2,
  Sparkles,
  Info,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import Sidebar from '../components/layout/Sidebar'
import { meetingsAPI, botAPI, cppBotAPI, agendaAPI, zoomAPI, dashboardAPI, getServerOrigin, analyticsAPI } from '../services/api'
import { useToast } from '../components/common/Toast'
import { useAuth } from '../context/AuthContext'
import { useBilling } from '../context/BillingContext'
import { useZoomOAuth } from '../context/ZoomOAuthContext'
import { decodeHtmlEntities } from '../utils/displayText'
import { MEETING_LIVE_INSIGHTS_TAB, getMeetingLiveInsightsPath } from '../utils/meetingRoutes'
import { launchZoomMeeting } from '../utils/zoomLaunch'
import { getPeriodLabelAndRange } from '../utils/periodDateRange'
import { markInvitationSeen, markPastDueSeen } from '../utils/invitationSeen'

import MeetingSetupOverlay from '../components/common/MeetingSetupOverlay'
import ChatWithRithmoPanel from '../components/meeting/ChatWithRithmoPanel'
import MeetingSearchBar from '../components/meeting/MeetingSearchBar'
import FollowupEmailDrafts from '../components/meeting/FollowupEmailDrafts'
import ParticipantEnrichment from '../components/meeting/ParticipantEnrichment'
import InternalAgendaSummaryPanel from '../components/meeting/InternalAgendaSummaryPanel'
// AgendaComposerV2Panel disabled in the agenda tab (duplicate of the main agenda list);
// import retained for easy re-enable. import AgendaComposerV2Panel from '../components/AgendaComposerV2Panel'
import ExecPreReadPanel from '../components/ExecPreReadPanel'
import DocumentIntelligencePanel from '../components/meeting/DocumentIntelligencePanel'
import AgendaTabDocumentsSection from '../components/meeting/AgendaTabDocumentsSection'
import MeetingLiveInsightsPanel from '../components/meeting/MeetingLiveInsightsPanel'
import MeetingScorecardsSection from '../components/analytics/MeetingScorecardsSection'
// import MemberPrivateGoalsPanel from '../components/meeting/MemberPrivateGoalsPanel'
import BriefReviewPanel from '../components/meeting/BriefReviewPanel'
import PreMeetingInsightsPanel from '../components/meeting/PreMeetingInsightsPanel'
import RecapPersonalSection from '../components/meeting/RecapPersonalSection'
import {
  fetchInternalOperatorBootstrap,
  postLateStartResolution,
  postLateStartUndo,
} from '../services/operatorInternalApi'
import { parseInternalOperatorRealtimeWsMessage } from '../hooks/useInternalOperatorRealtime'
import { parseLiveMeetingInsightsRealtimeWsMessage } from '../hooks/useLiveMeetingInsightsRealtime'
import { isExternalJoinEnabled } from '../config/externalJoinBeta'

/** Same session key as AnalyticsPage — series scorecard period stays in sync with Analytics. */
const SERIES_SCORECARD_TIMEFRAME_KEY = 'rithmo_analytics_timeframe'

const SCORECARD_DIMENSION_IDS = ['talk-equity', 'agenda-adherence', 'interruptions', 'decision-velocity', 'time-discipline']

function overallScoreFromMeetingScorecard(scorecard) {
  if (!scorecard || typeof scorecard !== 'object') return null
  const o = scorecard.overallScore
  if (typeof o === 'number' && Number.isFinite(o)) return Math.round(o)
  const vals = SCORECARD_DIMENSION_IDS.map((id) => scorecard[id]).filter(
    (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100
  )
  if (vals.length === 0) return null
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
}

function scheduledWallClockDurationMinutes(startIso, endIso) {
  if (!startIso || !endIso) return null
  const startMs = new Date(startIso).getTime()
  const endMs = new Date(endIso).getTime()
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null
  return Math.round((endMs - startMs) / 60000)
}

function formatWallClockDurationLabel(minutes) {
  if (minutes == null || minutes <= 0) return '—'
  if (minutes < 60) return `${minutes} min`
  const hrs = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (mins === 0) return hrs === 1 ? '1 hr' : `${hrs} hr`
  return hrs === 1 ? `1 hr ${mins} min` : `${hrs} hr ${mins} min`
}

function readStoredSeriesScorecardTimeframe() {
  try {
    const v = sessionStorage.getItem(SERIES_SCORECARD_TIMEFRAME_KEY)
    if (v === 'week' || v === 'month' || v === 'year' || v === 'all') return v
  } catch {
    // ignore
  }
  return 'month'
}

const SERIES_SCORECARD_PERIOD_MAP = { week: '7d', month: '30d', year: '12m', all: 'all' }

const SERIES_SCORECARD_DETAIL_PERIOD_LABELS = {
  week: 'Week avg',
  month: 'Month avg',
  year: 'Year avg',
  all: 'All-time avg',
}

function participantDisplayName(p) {
  const raw = decodeHtmlEntities(p.displayName ?? p.name ?? p.userName ?? p.user_name ?? p.email ?? '') || '—'
  if (raw === '—') return raw
  if (isPlaceholderName(raw)) return 'Guest'
  return raw
}

function isParticipantBot(p) {
  if (p.isBot) return true
  const n = (p.displayName ?? p.name ?? p.userName ?? p.user_name ?? '').toLowerCase()
  return ['rithmo', 'recall', 'zoom bot', 'fireflies'].some((b) => n.includes(b))
}

function isInvitedParticipantCurrentlyLive(p, actualEmailSet, actualFirstNameSet) {
  const email = (p.email ?? '').toLowerCase().trim()
  if (email && actualEmailSet.has(email)) return true
  const invitedDisplayName = (p.displayName ?? p.name ?? p.userName ?? p.user_name ?? '').trim()
  if (!invitedDisplayName) return false
  const firstName = invitedDisplayName.toLowerCase().split(/\s+/)[0]
  return firstName.length > 2 && actualFirstNameSet.has(firstName)
}

/** Do not show raw participant IDs (numeric, UUID) in Assignee column. */
function formatAssigneeForDisplay(owner) {
  if (!owner || typeof owner !== 'string') return ''
  const raw = String(owner).trim()
  if (!raw) return ''
  if (/^[0-9]+$/.test(raw)) return '' // Zoom participant IDs
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) return '' // UUIDs
  return raw
}

/** Render agenda item description respecting format: bullets (newline-separated) vs paragraph. */
function AgendaItemDescription({ text, className = '' }) {
  if (!text || !String(text).trim()) return null
  const str = String(text).trim()
  const asBullets = str.includes('\n')
  if (asBullets) {
    const lines = str.split('\n').filter((line) => line.trim() !== '')
    if (lines.length === 0) return null
    return (
      <ul className={`text-sm text-gray-500 mt-1 list-disc list-inside space-y-0.5 ${className}`}>
        {lines.map((line, i) => (
          <li key={i}>{decodeHtmlEntities(line.trim())}</li>
        ))}
      </ul>
    )
  }
  return (
    <p className={`text-sm text-gray-500 mt-1 whitespace-pre-line ${className}`}>
      {decodeHtmlEntities(str)}
    </p>
  )
}

/** Flatten Zoom/state-engine outputsByAgendaItem to rows matching recap output shape (live editing after agenda complete). */
function flattenOutputsByAgendaItem(outputsByAgendaItem, sourceMeetingId) {
  if (!outputsByAgendaItem || typeof outputsByAgendaItem !== 'object') return []
  const rows = []
  for (const [agendaKey, bucket] of Object.entries(outputsByAgendaItem)) {
    if (!bucket || typeof bucket !== 'object') continue
    const agendaItemId = agendaKey === 'meeting' ? null : agendaKey
    ;(bucket.decisions || []).forEach((o) => {
      if (o?.id && (o.summary ?? '').trim()) {
        rows.push({
          id: o.id,
          type: 'decision',
          summary: o.summary,
          owner: o.owner,
          agendaItemId,
          sourceMeetingId,
        })
      }
    })
    ;(bucket.actionItems || []).forEach((o) => {
      if (o?.id && (o.summary ?? '').trim()) {
        rows.push({
          id: o.id,
          type: 'action_item',
          summary: o.summary,
          owner: o.owner,
          dueDate: o.dueDate,
          agendaItemId,
          sourceMeetingId,
        })
      }
    })
    ;(bucket.followUps || []).forEach((o) => {
      if (o?.id && (o.summary ?? '').trim()) {
        rows.push({
          id: o.id,
          type: 'follow_up',
          summary: o.summary,
          owner: o.owner,
          dueDate: o.dueDate,
          agendaItemId,
          sourceMeetingId,
        })
      }
    })
  }
  return rows
}

/** Zoom default display names when user hasn't set a custom name. */
const ZOOM_PLACEHOLDER_NAME = /^participant \d+$/i

function isPlaceholderName(n) {
  return n && ZOOM_PLACEHOLDER_NAME.test(String(n).trim())
}

function preferDisplayName(a, b) {
  const aName = (a?.displayName ?? a?.name ?? a?.userName ?? a?.user_name ?? a?.email ?? '').trim()
  const bName = (b?.displayName ?? b?.name ?? b?.userName ?? b?.user_name ?? b?.email ?? '').trim()
  if (!aName) return bName
  if (!bName) return aName
  if (isPlaceholderName(aName) && !isPlaceholderName(bName)) return bName
  if (!isPlaceholderName(aName) && isPlaceholderName(bName)) return aName
  return aName
}

/**
 * Same person often appears twice in live data: C++ roster (Zoom userId) vs WS/meeting state
 * (participantUUID). First merge pass keys only by id — so two ids ⇒ two rows.
 * Second pass collapses by normalized display name (aligned with buildMeetingStatePayload).
 */
function dedupeHumansByNormalizedDisplayName(rows) {
  const seen = new Map()
  const result = []
  for (const p of rows) {
    const raw = String(p.displayName ?? p.name ?? p.userName ?? p.user_name ?? '').trim()
    const n = raw.toLowerCase()
    const bot =
      p.isBot === true ||
      ['rithmo', 'recall', 'zoom bot', 'fireflies'].some((b) => n.includes(b))
    if (bot || !raw || isPlaceholderName(raw)) {
      result.push(p)
      continue
    }
    const key = raw.replace(/\s+/g, ' ').trim().toLowerCase()
    const idx = seen.get(key)
    if (idx === undefined) {
      seen.set(key, result.length)
      result.push({ ...p })
    } else {
      const existing = result[idx]
      result[idx] = {
        ...existing,
        ...p,
        displayName: preferDisplayName(existing, p),
        name: preferDisplayName(existing, p),
        participantId: existing.participantId ?? p.participantId,
        id: existing.id ?? p.id,
        speakingTime: Math.max(existing.speakingTime ?? 0, p.speakingTime ?? 0),
        interruptions: Math.max(existing.interruptions ?? 0, p.interruptions ?? 0),
        joinedAtMs: existing.joinedAtMs ?? p.joinedAtMs,
        leftAtMs: existing.leftAtMs ?? p.leftAtMs,
      }
    }
  }
  return result
}

/** Merge participants from C++ bot and WebSocket; preserve leftAtMs; never remove participants who left. */
function mergeParticipants(prev, incoming) {
  if (!Array.isArray(incoming) || incoming.length === 0) return prev ?? []
  const byKey = new Map()
  const add = (p) => {
    const pid = String(p.participantId ?? p.id ?? '').trim()
    const name = (p.displayName ?? p.name ?? p.userName ?? p.user_name ?? p.email ?? '').trim().toLowerCase()
    const key = pid ? `id:${pid}` : (name || `n:${pid || 'anon'}`)
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, { ...p })
    } else {
      const bestDisplayName = preferDisplayName(existing, p)
      const fallbackDisplayName = p.displayName ?? p.name ?? p.userName ?? p.user_name ?? p.email
      const fallbackName = p.name ?? p.displayName ?? p.userName ?? p.user_name ?? p.email
      const mergedDisplayName = (bestDisplayName || existing.displayName) ?? fallbackDisplayName
      const mergedName = (bestDisplayName || existing.name) ?? fallbackName
      byKey.set(key, {
        ...existing,
        ...p,
        displayName: mergedDisplayName,
        name: mergedName,
        joinedAtMs: existing.joinedAtMs ?? p.joinedAtMs,
        leftAtMs: existing.leftAtMs ?? p.leftAtMs,
        speakingTime: Math.max(existing.speakingTime ?? 0, p.speakingTime ?? 0),
      })
    }
  }
  ;(prev ?? []).forEach(add)
  incoming.forEach(add)
  return dedupeHumansByNormalizedDisplayName(Array.from(byKey.values()))
}

function MeetingElapsedTimer({ startedAtMs, controlsTabActive }) {
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  const diffMin = Math.max(0, Math.round((now - startedAtMs) / 60_000))
  const label = diffMin < 60 ? `${diffMin}m` : `${Math.floor(diffMin / 60)}h ${diffMin % 60}m`

  return (
    <div className={`inline-flex items-center gap-1.5 text-gray-500 ${controlsTabActive ? 'text-2xl lg:text-[2rem]' : 'text-3xl'}`}>
      <Clock className="h-5 w-5 lg:h-6 lg:w-6" />
      <span className="font-bold tabular-nums">{label}</span>
      <span className="text-sm font-normal text-gray-400">elapsed</span>
    </div>
  )
}

function MeetingDetailPage() {
  const { meetingId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()

  const [meeting, setMeeting] = useState(null)
  const [parentMeetingForPrep, setParentMeetingForPrep] = useState(null)
  const [activeTab, setActiveTab] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get('tab')
    if (tab === MEETING_LIVE_INSIGHTS_TAB) return MEETING_LIVE_INSIGHTS_TAB
    if (tab === 'agenda') return 'agenda' // ?tab=agenda opens Prep (dashboard "Today" cards)
    return 'overview'
  })
  const [loading, setLoading] = useState(true)

  // Live meeting state
  const [isLive, setIsLive] = useState(false)
  const [liveState, setLiveState] = useState(null)
  const [participants, setParticipants] = useState([])
  // Invited participants from the DB invite list — kept separate from live participants
  // so the Expected tab always shows who was invited regardless of live state
  const [invitedParticipants, setInvitedParticipants] = useState([])
  const [participantContextRows, setParticipantContextRows] = useState([])
  const [participantContextLoading, setParticipantContextLoading] = useState(false)
  const [participantContextError, setParticipantContextError] = useState(null)
  const [scrubbingParticipantContextIds, setScrubbingParticipantContextIds] = useState(() => new Set())
  const [participantPrepProfiles, setParticipantPrepProfiles] = useState([])
  const [participantPrepProfilesVersion, setParticipantPrepProfilesVersion] = useState(2)
  const [participantPrepProfilesLoading, setParticipantPrepProfilesLoading] = useState(false)
  const [participantPrepProfilesError, setParticipantPrepProfilesError] = useState(null)
  const [prepStrategyContext, setPrepStrategyContext] = useState([])
  // Per-participant manual commitment order for the Prep Notes card: a Map of
  // participantId -> ordered commitment ids (top first). Applied at render so a
  // drag-reorder sticks without a refetch (which would re-sort to the default).
  const [prepManualOrder, setPrepManualOrder] = useState(() => new Map())
  const [liveInsights, setLiveInsights] = useState(null)
  const [liveInsightsLoading, setLiveInsightsLoading] = useState(false)
  const [liveInsightsError, setLiveInsightsError] = useState(null)
  const [memberPrivateGoalsState, setMemberPrivateGoalsState] = useState(null)
  const [memberPrivateGoalsLoading, setMemberPrivateGoalsLoading] = useState(false)
  const [memberPrivateGoalsError, setMemberPrivateGoalsError] = useState(null)
  const [agendaStatus, setAgendaStatus] = useState(null)
  const [botStatus, setBotStatus] = useState(null)

  // Action loading states
  const [actionLoading, setActionLoading] = useState({
    mute: null,
    advance: false,
    end: false,
  })

  // WebSocket push state
  const wsRef = useRef(null)
  const [wsConnected, setWsConnected] = useState(false)

  /** INTERNAL_OPERATOR_REALTIME_V1 bootstrap + WS merges (not MEETING_STATE_UPDATE). */
  const [ioEnvelope, setIoEnvelope] = useState(null)
  const [ioError, setIoError] = useState(null)
  const [ioLoading, setIoLoading] = useState(false)

  // Recurring-series overview: same Meeting Scorecards section as Analytics (scoped to occurrences)
  const [seriesAnalyticsTimeframe, setSeriesAnalyticsTimeframe] = useState(readStoredSeriesScorecardTimeframe)
  const [seriesAnalytics, setSeriesAnalytics] = useState(null)
  const [seriesAnalyticsLoading, setSeriesAnalyticsLoading] = useState(false)

  const setSeriesAnalyticsTimeframePersist = useCallback((id) => {
    setSeriesAnalyticsTimeframe(id)
    try {
      sessionStorage.setItem(SERIES_SCORECARD_TIMEFRAME_KEY, id)
    } catch {
      // ignore
    }
  }, [])

  // TTS input

  // Editable agenda for recurring parent (inline edit on Agenda tab)
  const [editableAgenda, setEditableAgenda] = useState([])
  const [agendaSaving, setAgendaSaving] = useState(false)
  const [isEditingAgenda, setIsEditingAgenda] = useState(false)
  const [agendaDocumentsVersion, setAgendaDocumentsVersion] = useState(0)
  const bumpAgendaDocuments = useCallback(() => setAgendaDocumentsVersion((v) => v + 1), [])

  // "Add Rithmo Meeting Manager" (Option 1) state – populated when navigating from AgendaBuilder
  const navState = location.state || {}
  const [addBotJoinUrl] = useState(
    typeof navState.joinUrl === 'string' && navState.joinUrl.length > 0 ? navState.joinUrl : null
  )
  const [addBotPassword] = useState(
    typeof navState.password === 'string' && navState.password.length > 0 ? navState.password : null
  )
  const [addBotEligible] = useState(
    Boolean(navState.createdWithUserZoom || navState.addBotAfterYouJoin)
  )
  const [cppBotJoined, setCppBotJoined] = useState(Boolean(navState.cppBotJoined))
  const [addingBot, setAddingBot] = useState(false)

  // Recap, outputs, and series (for ended / recurring meetings)
  const [recapOutputs, setRecapOutputs] = useState([])
  // Viewer-private post-meeting coaching: { tips, scorecard, communicationStyle, tone }
  const [coaching, setCoaching] = useState(null)
  const [recapNarrativeSummary, setRecapNarrativeSummary] = useState(null)
  // Map<outputId, closedAtMs | null> — null means finished but no timestamp (legacy rows)
  const [closedOutputIds, setClosedOutputIds] = useState(new Map())
  const [seriesInstances, setSeriesInstances] = useState([])
  const [seriesInstancesLoading, setSeriesInstancesLoading] = useState(false)
  // Number of *categories* visible before "See more" — not number of items.
  // Start at 2 so Decisions + Action Items are always fully visible; Follow Ups collapse.
  const [outstandingExpandCount, setOutstandingExpandCount] = useState(2)
  const [finishedExpandCount, setFinishedExpandCount] = useState(2)
  const [outputStatusLoading, setOutputStatusLoading] = useState({})
  const [expandedModels, setExpandedModels] = useState([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingMeeting, setDeletingMeeting] = useState(false)
  const [editingOutputId, setEditingOutputId] = useState(null)
  const [editingSummary, setEditingSummary] = useState('')
  const [editingOwner, setEditingOwner] = useState('')
  const [outputContentLoading, setOutputContentLoading] = useState({})
  const [showShareMenu, setShowShareMenu] = useState(false)
  const [showEmailReportModal, setShowEmailReportModal] = useState(false)
  const [shareEmailTo, setShareEmailTo] = useState('')
  const [shareEmailSending, setShareEmailSending] = useState(false)
  const shareMenuRef = useRef(null)
  const [agendaLens, setAgendaLens] = useState('public')
  const [disabledLiveControlHintArea, setDisabledLiveControlHintArea] = useState(null)

  const { user } = useAuth()
  const billing = useBilling()
  const { isConnected: zoomConnected, connectZoomUrl } = useZoomOAuth()
  const isMeetingOrganizer = Boolean(
    user?.id && meeting && String(user.id) === String(meeting.userId ?? meeting.ownerId)
  )
  const controlsTabActive = activeTab === 'controls'
  const compactLiveControlsMode = false


  const userTz = user?.timezone?.trim() || Intl.DateTimeFormat().resolvedOptions().timeZone
  const fmtTime = (dateInput) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: userTz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'shortGeneric',
    }).format(typeof dateInput === 'number' ? new Date(dateInput) : new Date(dateInput))

  // Mark "new invitation" and "past due" as seen when user views meeting (clicked into)
  useEffect(() => {
    if (meetingId) {
      markInvitationSeen(meetingId)
      markPastDueSeen(meetingId)
    }
  }, [meetingId])

  // Fetch meeting data from backend (DB record or live state engine)
  const fetchMeetingData = useCallback(async () => {
    try {
      const response = await meetingsAPI.get(meetingId)
      const data = response?.data ?? response
      const liveMeeting = data?.meeting
      const fromDb = data?.meetingId && !liveMeeting
      if (liveMeeting) {
        setLiveState(data)
        setIsLive(liveMeeting.status === 'in-progress' || liveMeeting.status === 'active')
        // Live path: data.participants is the live C++ bot list (current attendees).
        // Separately fetch the DB record to get the invited/expected participant list.
        const liveParticipants = data.participants ?? []
        const mergedLive = mergeParticipants([], liveParticipants)
        setParticipants(mergedLive)
        setInvitedParticipants(
          Array.isArray(data.expectedParticipants) ? data.expectedParticipants : data.participants ?? []
        )
        setMeeting((prev) => {
          const resolvedAudience =
            data.audienceMode === 'external'
              ? 'external'
              : data.audienceMode === 'internal'
                ? 'internal'
                : prev?.audienceMode === 'external'
                  ? 'external'
                  : 'internal'
          return {
            id: meetingId,
            meetingId: data.meetingId ?? meetingId,
            title: liveMeeting.topic ?? `Meeting ${data.meetingId ?? meetingId}`,
            description: liveMeeting.description ?? '',
            status: liveMeeting.status,
            participants: mergedLive,
            expectedParticipants: data.expectedParticipants,
            agenda: data.agenda ?? [],
            governanceSettings: data.policy ?? {},
            audienceMode: resolvedAudience,
          }
        })
      } else if (fromDb) {
        setLiveState(data)
        setIsLive(data.status === 'live' || data.status === 'starting')
        const agenda = data.agenda ?? []
        const totalDuration = agenda.reduce((sum, item) => sum + (item.durationSec ? Math.round(item.durationSec / 60) : item.duration ?? 0), 0)
        const isEnded = (data.status ?? '') === 'ended'
        setMeeting({
          id: meetingId,
          meetingId: data.meetingId ?? meetingId,
          title: data.title ?? `Meeting ${data.meetingId ?? meetingId}`,
          description: data.description ?? '',
          status: data.status ?? 'created',
          startTime: data.scheduledStartTime ?? data.startTime,
          endTime: data.scheduledEndTime ?? data.endTime,
          scheduledStartTime: data.scheduledStartTime,
          scheduledEndTime: data.scheduledEndTime,
          startedAtMs: data.startedAtMs ?? null,
          endedAtMs: data.endedAtMs ?? null,
          duration: totalDuration || undefined,
          participants: data.participants ?? [],
          agenda,
          governanceSettings: data.governanceConfig ?? {},
          joinUrl: data.joinUrl,
          meetingUrl: data.joinUrl,
          password: data.password,
          provider: data.provider,
          meetingJoinKind: data.meetingJoinKind ?? 'hosted',
          isRecurring: Boolean(data.isRecurring),
          parentMeetingId: data.parentMeetingId ?? undefined,
          recurringSeriesId: data.recurringSeriesId,
          recurrenceRule: data.recurrenceRule,
          providerMeetingType: data.providerMeetingType,
          scorecard: data.scorecard ?? undefined,
          hasActiveChild: data.hasActiveChild ?? undefined,
          userId: data.userId ?? data.ownerId,
          ownerId: data.ownerId ?? data.userId,
          expectedParticipants: data.expectedParticipants,
          audienceMode: data.audienceMode === 'external' ? 'external' : 'internal',
          isImported: Boolean(data.isImported),
          calendarProvider: data.calendarProvider ?? undefined,
          calendarEventId: data.calendarEventId ?? undefined,
        })
        // invitedParticipants = scheduled roster only (expectedParticipants from API), not merged live joiners.
        // participants state = live C++ bot / WS data only — do NOT seed from DB list so
        // invited-but-not-joined people never appear in the live Actual section.
        setInvitedParticipants(
          Array.isArray(data.expectedParticipants) ? data.expectedParticipants : data.participants ?? []
        )
        const isParent = Boolean(data.isRecurring && !data.parentMeetingId)
        const isChild = Boolean(data.parentMeetingId)
        // When a parent page has an active child occurrence, redirect to it so
        // participants get the live experience (same as the organizer redirect).
        if (isParent && data.activeChildMeetingId) {
          navigate(`/meeting/${data.activeChildMeetingId}`, { replace: true })
          return
        }
        const shouldFetchOutputs = isEnded || isParent
        if (shouldFetchOutputs) {
          meetingsAPI.getClosedOutputs(meetingId).then((res) => {
            const withTs = res?.data?.closedOutputsWithTimestamp ?? res?.closedOutputsWithTimestamp ?? null
            const list = res?.data?.closedOutputIds ?? res?.closedOutputIds ?? []
            if (withTs && typeof withTs === 'object') {
              setClosedOutputIds(new Map(Object.entries(withTs)))
            } else {
              // Backwards compat: server returned only IDs (no timestamps)
              setClosedOutputIds(new Map(list.map((id) => [id, null])))
            }
          }).catch(() => {})
          if (Boolean(data.isRecurring)) {
            setSeriesInstancesLoading(true)
            meetingsAPI.getSeriesInstances(meetingId).then(async (res) => {
              const list = res?.data?.meetings ?? res?.meetings ?? []
              const instances = Array.isArray(list) ? list : []
              setSeriesInstances(instances)
              setSeriesInstancesLoading(false)
              if (isParent) {
                // Batch endpoint: one call returns details for every occurrence,
                // replacing the previous N+1 fan-out (one /details per instance).
                try {
                  // When a child occurrence is live, skip loading old recap outputs —
                  // the live WS stream will supply current-session outputs directly.
                  // Loading historical outputs here causes participants (who view the
                  // parent page) to see stale decisions / action items from past occurrences.
                  const activeStatuses = ['live', 'starting', 'paused', 'ending']
                  const activeChild = instances.find((inst) =>
                    activeStatuses.includes(inst.status || '')
                  )
                  const activeChildId = activeChild?.id ?? activeChild?.meetingId ?? null

                  if (!activeChildId) {
                    const batch = await meetingsAPI.getSeriesDetails(meetingId)
                    const batchData = batch?.data ?? batch
                    const batchInstances = Array.isArray(batchData?.instances) ? batchData.instances : []
                    const detailsByMeetingId = new Map(
                      batchInstances.map((b) => [b.meeting_id, b])
                    )
                    const allOutputs = []
                    for (const inst of instances) {
                      const instId = inst.id ?? inst.meetingId
                      const instDetails = detailsByMeetingId.get(instId)
                      if (!instDetails) continue
                      const recap = instDetails?.latest_recap
                      const content = recap?.content
                      const outs = Array.isArray(content?.outputs) ? content.outputs : []
                      // Resolve the child occurrence date for display in the outputs list.
                      // Prefer actual start time; fall back to scheduled start.
                      const instSourceDate =
                        inst.startedAtMs
                          ? new Date(inst.startedAtMs)
                          : inst.scheduledStartTime
                            ? new Date(inst.scheduledStartTime)
                            : inst.startTime
                              ? new Date(inst.startTime)
                              : null
                      outs.forEach((o) =>
                        allOutputs.push({
                          ...o,
                          sourceMeetingId: instId,
                          sourceDate: instSourceDate,
                        })
                      )
                    }
                    setRecapOutputs((prev) => (allOutputs.length > 0 ? allOutputs : prev))
                  }
                } catch (_) {}
              }
            }).catch(() => {
              setSeriesInstancesLoading(false)
            })
          }
          if (isEnded && isChild) {
            meetingsAPI.getDetails(meetingId).then((detailsRes) => {
              const details = detailsRes?.data ?? detailsRes
              const analyticsPayload = details?.analytics ?? details?.latest_analytics_rollup?.content
              const recap = details?.latest_recap
              const content = recap?.content
              const outputs = Array.isArray(content?.outputs) ? content.outputs : []
              setRecapOutputs((prev) => (outputs.length > 0 ? outputs.map((o) => ({ ...o, sourceMeetingId: meetingId })) : prev))
              const narrative = content?.summary?.trim()
              if (narrative && narrative !== 'Meeting recap') setRecapNarrativeSummary(narrative)
              setMeeting((prev) => {
                if (!prev) return prev
                const next = { ...prev, analytics: typeof analyticsPayload === 'object' ? analyticsPayload : prev.analytics }
                if (analyticsPayload && typeof analyticsPayload === 'object') next.analytics = analyticsPayload
                return next
              })
            }).catch(() => {})
          }
          if (isEnded && !isChild && !isParent) {
            meetingsAPI.getDetails(meetingId).then((detailsRes) => {
              const details = detailsRes?.data ?? detailsRes
              const analyticsPayload = details?.analytics ?? details?.latest_analytics_rollup?.content
              const recap = details?.latest_recap
              const content = recap?.content
              const outputs = Array.isArray(content?.outputs) ? content.outputs : []
              setRecapOutputs((prev) => (outputs.length > 0 ? outputs.map((o) => ({ ...o, sourceMeetingId: meetingId })) : prev))
              const narrative = content?.summary?.trim()
              if (narrative && narrative !== 'Meeting recap') setRecapNarrativeSummary(narrative)
              setMeeting((prev) => {
                if (!prev) return prev
                const next = { ...prev, analytics: typeof analyticsPayload === 'object' ? analyticsPayload : prev.analytics }
                if (analyticsPayload && typeof analyticsPayload === 'object') next.analytics = analyticsPayload
                return next
              })
            }).catch(() => {})
          }
          // Viewer-private coaching (Tips / Personal Scorecard / Your Tone) for the
          // Recap tab. Only meaningful for ended individual/child meetings; the
          // endpoint returns an empty payload for parents anyway.
          if (isEnded && !isParent) {
            meetingsAPI.getRecapPersonal(meetingId)
              .then((res) => setCoaching(res?.data ?? res ?? null))
              .catch(() => {})
          }
        } else {
          setRecapOutputs([])
          setCoaching(null)
          setClosedOutputIds(new Map())
          setSeriesInstances([])
          setSeriesInstancesLoading(false)
        }
      } else {
        setMeeting(null)
      }
    } catch {
      setMeeting(null)
    }
  }, [meetingId])

  const fetchParticipantContextMemory = useCallback(async () => {
    if (!meetingId) return
    setParticipantContextLoading(true)
    setParticipantContextError(null)
    try {
      const response = await meetingsAPI.getParticipantContextMemory(meetingId)
      const data = response?.data ?? response
      const rows = Array.isArray(data?.participants) ? data.participants : []
      const normalized = rows
        .map((row) => {
          const bulletItemsFromApi = Array.isArray(row?.contextBulletItems)
            ? row.contextBulletItems
                .filter((item) => typeof item?.text === 'string' && item.text.trim() !== '')
                .map((item) => ({
                  memoryId:
                    typeof item?.memoryId === 'string' && item.memoryId.trim()
                      ? item.memoryId.trim()
                      : null,
                  memoryType: typeof item?.memoryType === 'string' ? item.memoryType : null,
                  text: decodeHtmlEntities(item.text.trim()),
                }))
                .slice(0, 5)
            : []
          const fallbackBullets = Array.isArray(row?.contextBullets)
            ? row.contextBullets
                .filter((b) => typeof b === 'string' && b.trim() !== '')
                .map((b) => ({
                  memoryId: null,
                  text: decodeHtmlEntities(b.trim()),
                }))
                .slice(0, 5)
            : []
          const bulletItems = bulletItemsFromApi.length > 0 ? bulletItemsFromApi : fallbackBullets
          return {
            participantId: typeof row?.participantId === 'string' ? row.participantId : null,
            userId: typeof row?.userId === 'string' ? row.userId : null,
            displayName:
              typeof row?.displayName === 'string' && row.displayName.trim()
                ? decodeHtmlEntities(row.displayName.trim())
                : null,
            contextBullets: bulletItems.map((item) => item.text),
            contextBulletItems: bulletItems,
          }
        })
        .filter((row) => row.participantId && row.contextBulletItems.length > 0)
      setParticipantContextRows(normalized)
    } catch (err) {
      setParticipantContextRows([])
      setParticipantContextError(err instanceof Error ? err.message : 'Failed to load participant context')
    } finally {
      setParticipantContextLoading(false)
    }
  }, [meetingId])

  const fetchParticipantPrepProfiles = useCallback(async () => {
    if (!meetingId) return
    setParticipantPrepProfilesLoading(true)
    setParticipantPrepProfilesError(null)
    try {
      const response = await meetingsAPI.getParticipantPrepProfiles(meetingId)
      const data = response?.data ?? response
      const rows = Array.isArray(data?.participants) ? data.participants : []
      setParticipantPrepProfilesVersion(data?.version === 1 ? 1 : 2)
      setParticipantPrepProfiles(rows)
      setPrepStrategyContext(Array.isArray(data?.strategyContext) ? data.strategyContext : [])
      // Server now carries the persisted order (via manualRank); drop optimistic
      // local overrides so the freshly-fetched order is authoritative.
      setPrepManualOrder(new Map())
    } catch (err) {
      setParticipantPrepProfiles([])
      setPrepStrategyContext([])
      setParticipantPrepProfilesError(err instanceof Error ? err.message : 'Failed to load participant profiles')
    } finally {
      setParticipantPrepProfilesLoading(false)
    }
  }, [meetingId])

  const scrubParticipantContextMemory = useCallback(async (participantId, bullet) => {
    if (!meetingId || !participantId || !bullet?.memoryId) return
    const memoryId = bullet.memoryId
    setScrubbingParticipantContextIds((prev) => {
      const next = new Set(prev)
      next.add(memoryId)
      return next
    })
    try {
      await meetingsAPI.deleteParticipantContextMemory(meetingId, participantId, memoryId)
      setParticipantContextRows((prev) =>
        prev
          .map((row) => {
            if (row.participantId !== participantId) return row
            const nextItems = (row.contextBulletItems ?? []).filter((item) => item.memoryId !== memoryId)
            return {
              ...row,
              contextBulletItems: nextItems,
              contextBullets: nextItems.map((item) => item.text),
            }
          })
          .filter((row) => (row.contextBulletItems ?? []).length > 0),
      )
      toast.success('Insight removed', 'It will no longer appear in Personal Insights.')
    } catch (err) {
      toast.error('Remove failed', err instanceof Error ? err.message : 'Could not remove this personal insight.')
    } finally {
      setScrubbingParticipantContextIds((prev) => {
        const next = new Set(prev)
        next.delete(memoryId)
        return next
      })
    }
  }, [meetingId, toast])

  const fetchLiveInsights = useCallback(async () => {
    if (!meetingId) return
    setLiveInsightsLoading(true)
    setLiveInsightsError(null)
    try {
      const response = await meetingsAPI.getLiveInsights(meetingId)
      const data = response?.data ?? response
      setLiveInsights(data ?? null)
    } catch (err) {
      if (err instanceof Error && err.status === 404) {
        setLiveInsights(null)
        setLiveInsightsError(null)
        return
      }
      setLiveInsights(null)
      setLiveInsightsError(err instanceof Error ? err.message : 'Failed to load live insights')
    } finally {
      setLiveInsightsLoading(false)
    }
  }, [meetingId])

  const fetchMemberPrivateGoals = useCallback(async () => {
    if (!meetingId) return
    setMemberPrivateGoalsLoading(true)
    setMemberPrivateGoalsError(null)
    try {
      const response = await meetingsAPI.getMemberPrivateGoals(meetingId)
      const data = response?.data ?? response
      setMemberPrivateGoalsState(data?.memberPrivateGoals ?? null)
    } catch (err) {
      setMemberPrivateGoalsState(null)
      setMemberPrivateGoalsError(err instanceof Error ? err.message : 'Failed to load your private goals')
    } finally {
      setMemberPrivateGoalsLoading(false)
    }
  }, [meetingId])

  const postSharedLiveInsightAction = useCallback(async (payload) => {
    if (!meetingId) return
    try {
      await meetingsAPI.postSharedLiveInsightAction(meetingId, payload)
      if (!wsConnected) {
        await fetchLiveInsights()
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update shared team insight')
    }
  }, [fetchLiveInsights, meetingId, toast, wsConnected])

  const postPersonalLiveInsightAction = useCallback(async (payload) => {
    if (!meetingId) return
    try {
      await meetingsAPI.postPersonalLiveInsightAction(meetingId, payload)
      if (!wsConnected) {
        await fetchLiveInsights()
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update private insight')
    }
  }, [fetchLiveInsights, meetingId, toast, wsConnected])

  const saveMemberPrivateGoals = useCallback(async (payload) => {
    if (!meetingId) return
    try {
      const response = await meetingsAPI.putMemberPrivateGoals(meetingId, payload)
      const data = response?.data ?? response
      setMemberPrivateGoalsState(data?.memberPrivateGoals ?? payload)
      setMemberPrivateGoalsError(null)
      if (!wsConnected) {
        await fetchLiveInsights()
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save your private goals')
    }
  }, [fetchLiveInsights, meetingId, toast, wsConnected])

  const renderPersonalInsightsCard = () => (
    <motion.div className="glass rounded-2xl p-6 h-full flex flex-col min-w-0 min-h-[280px]">
      <div className="flex items-start justify-between gap-2 mb-4 shrink-0">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 shrink-0 text-primary-600" />
            Personal Insights
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Product-approved personal context recalled from prior meetings to help you prepare.
          </p>
        </div>
        <button
          onClick={() => void fetchParticipantContextMemory()}
          disabled={participantContextLoading}
          className="shrink-0 p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-60"
          title="Refresh personal insights"
        >
          <RefreshCw className={`w-4 h-4 ${participantContextLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
      {participantContextLoading ? (
        <div className="flex flex-1 items-center justify-center gap-2 py-4 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading personal insights...</span>
        </div>
      ) : participantContextError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{participantContextError}</p>
        </div>
      ) : (() => {
        const otherParticipantRows = participantContextRows.filter(
          (row) => !(row.userId && user?.id && String(row.userId) === String(user.id))
        )
        return otherParticipantRows.length === 0 ? (
        <div className="flex-1 rounded-xl border border-dashed border-gray-200 bg-gray-50/50 p-4 text-center flex flex-col justify-center">
          <p className="text-sm font-medium text-gray-600 mb-1">No personal insights yet</p>
          <p className="text-xs text-gray-400 leading-relaxed">
            Personal insights build up automatically as you attend meetings together.
            After a few meetings, you'll see personal context like hobbies, family updates,
            and life events that participants have shared — helping you connect on a personal level.
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 space-y-3 overflow-y-auto pr-1">
          {otherParticipantRows.map((row) => (
            <div
              key={row.participantId}
              className="p-3 rounded-xl bg-gray-50 min-w-0"
            >
              <div className="flex items-center gap-3 mb-2 min-w-0">
                <div className="avatar text-sm shrink-0">
                  {(row.displayName ?? 'P').charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-medium text-gray-900 truncate">
                  {row.displayName || 'Participant'}
                </span>
              </div>
              <ul className="space-y-1">
                {(row.contextBulletItems ?? []).slice(0, 5).map((bullet, idx) => {
                  const isScrubbing = bullet.memoryId ? scrubbingParticipantContextIds.has(bullet.memoryId) : false
                  const typeLabel =
                    typeof bullet.memoryType === 'string' && bullet.memoryType
                      ? bullet.memoryType.replace(/_/g, ' ')
                      : null
                  return (
                    <li key={bullet.memoryId ?? `${row.participantId}-${idx}`} className="text-sm text-gray-700 flex items-start gap-2 min-w-0">
                      <span className="text-primary-600 mt-0.5 shrink-0">•</span>
                      <span className="flex-1 min-w-0 break-words">
                        {bullet.text}
                        {typeLabel ? (
                          <span className="block text-[10px] uppercase tracking-wide text-gray-400 mt-0.5">{typeLabel}</span>
                        ) : null}
                      </span>
                      {bullet.memoryId ? (
                        <button
                          type="button"
                          onClick={() => void scrubParticipantContextMemory(row.participantId, bullet)}
                          disabled={isScrubbing}
                          className="shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-white hover:text-gray-800 disabled:opacity-60"
                          title="Remove this personal insight"
                        >
                          {isScrubbing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          <span className="hidden sm:inline">{isScrubbing ? 'Removing' : 'Remove'}</span>
                        </button>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      )
      })()}
      </div>
    </motion.div>
  )

  const VERBOSITY_LABELS = { concise: 'Concise', moderate: 'Moderate', detailed: 'Detailed' }
  const INTERRUPT_LABELS = {
    frequent_interrupter: 'Frequent interrupter',
    occasionally_interrupted: 'Occasionally interrupted',
    balanced: 'Balanced',
  }
  const ENGAGEMENT_LABELS = {
    increasing: 'Increasing',
    steady: 'Steady',
    declining: 'Declining',
    new_participant: 'New',
  }
  const ENGAGEMENT_COLORS = {
    increasing: 'text-green-600',
    steady: 'text-gray-600',
    declining: 'text-amber-600',
    new_participant: 'text-blue-600',
  }

  // Persist the user's manual drag-reorder of a person's commitments. Optimistic:
  // we hold the new order locally (applied at render) and fire-and-forget the
  // save; no refetch, which would re-sort back to the default order.
  const handleCommitmentReorder = (participantId, orderedIds) => {
    setPrepManualOrder((prev) => {
      const next = new Map(prev)
      next.set(participantId, orderedIds)
      return next
    })
    meetingsAPI.setCommitmentOrder(orderedIds).catch((err) => {
      toast.error('Could not save order', err instanceof Error ? err.message : undefined)
    })
  }

  // Priority badge for a commitment row, driven by the shared ranker's reason codes.
  const renderPrepCommitmentBadge = (item) => {
    const reasons = item.priorityReasons ?? []
    if (item.isOverdue || reasons.includes('overdue')) {
      return (
        <span className="ml-1.5 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-600 whitespace-nowrap">
          Overdue{item.dueDateText ? ` · ${item.dueDateText}` : ''}
        </span>
      )
    }
    if (reasons.includes('priority')) {
      return <span className="ml-1.5 rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">Priority</span>
    }
    if (reasons.includes('urgent')) {
      return <span className="ml-1.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">Urgent</span>
    }
    if (reasons.includes('blocked')) {
      return <span className="ml-1.5 rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">Blocked</span>
    }
    if (item.dueDateText) {
      return <span className="ml-1.5 text-xs text-gray-400">due {item.dueDateText}</span>
    }
    return null
  }

  const renderPrepCommitmentRow = (item, side) => {
    const reasons = item.priorityReasons ?? []
    const timeColor = item.isOverdue
      ? 'text-red-500'
      : reasons.includes('due_now') || reasons.includes('due_soon')
      ? 'text-amber-500'
      : 'text-gray-400'
    return (
      <div className="flex items-start gap-2 text-sm">
        {/* Drag handle sits right beside the copy so the reorder affordance is
            obvious. The whole row is draggable (Reorder.Item); the grip cues it. */}
        <span
          className="shrink-0 mt-0.5 text-gray-300 cursor-grab active:cursor-grabbing hover:text-gray-500 transition-colors"
          title="Drag to reorder"
          aria-hidden="true"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </span>
        <Clock className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${timeColor}`} />
        <div className="min-w-0 flex-1">
          <span className="text-gray-700 break-words">
            <span className="font-medium text-gray-500">{side === 'they' ? 'They owe:' : 'You owe:'}</span>{' '}
            {item.summary}
          </span>
          {renderPrepCommitmentBadge(item)}
        </div>
      </div>
    )
  }

  // Max commitments shown per person. The rest are intentionally hidden here
  // (they live in the Open Commitments view); we never show a long list.
  const PREP_COMMITMENT_DISPLAY = 5
  // Effective default order: the user's persisted manual rank first (ascending),
  // then the autonomous priority score. Mirrors the backend rule.
  const compareByEffectiveRank = (a, b) => {
    const ra = a.manualRank
    const rb = b.manualRank
    if (ra != null && rb != null && ra !== rb) return ra - rb
    if (ra != null && rb == null) return -1
    if (ra == null && rb != null) return 1
    return (b.priorityScore ?? 0) - (a.priorityScore ?? 0)
  }
  const renderPrepCardV2 = (profile) => {
    // Merge both sides into one list, deduped by commitment id.
    const byId = new Map()
    for (const it of [
      ...(profile.theyOwe ?? []).map((it) => ({ ...it, side: 'they' })),
      ...(profile.youOwe ?? []).map((it) => ({ ...it, side: 'you' })),
    ]) {
      if (it.id && !byId.has(it.id)) byId.set(it.id, it)
    }
    const items = Array.from(byId.values())

    // Apply an optimistic local drag order if the user just reordered this card;
    // otherwise fall back to the effective default order.
    const localOrder = prepManualOrder.get(profile.participantId)
    let ordered
    if (localOrder && localOrder.length) {
      const pos = new Map(localOrder.map((id, i) => [id, i]))
      ordered = items.slice().sort((a, b) => {
        const pa = pos.has(a.id) ? pos.get(a.id) : Infinity
        const pb = pos.has(b.id) ? pos.get(b.id) : Infinity
        if (pa !== pb) return pa - pb
        return compareByEffectiveRank(a, b)
      })
    } else {
      ordered = items.slice().sort(compareByEffectiveRank)
    }

    const hasOpen = ordered.length > 0
    const visibleOwed = ordered.slice(0, PREP_COMMITMENT_DISPLAY)
    return (
      <div
        key={profile.participantId}
        className="rounded-xl border border-gray-100 bg-white/60 p-4 min-w-0 space-y-3"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="avatar text-sm shrink-0">
            {(profile.displayName ?? 'P').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium text-gray-900 block truncate">
              {profile.displayName || 'Participant'}
            </span>
            {(profile.sharedMeetingCount ?? 0) > 0 ? (
              <span className="text-xs text-gray-500">
                {profile.sharedMeetingCount} shared meeting{profile.sharedMeetingCount === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>
        </div>

        {(profile.whoTheyAre?.length ?? 0) > 0 ? (
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
              <Info className="w-3.5 h-3.5" /> Who they are
            </div>
            <ul className="space-y-0.5">
              {profile.whoTheyAre.map((f, i) => (
                <li key={i} className="text-sm text-gray-700 break-words pl-1">• {f.text}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {hasOpen ? (
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
              <ClipboardCheck className="w-3.5 h-3.5" /> Open between you
            </div>
            <Reorder.Group
              axis="y"
              values={visibleOwed.map((it) => it.id)}
              onReorder={(ids) => handleCommitmentReorder(profile.participantId, ids)}
              className="space-y-1 list-none m-0 p-0"
            >
              {visibleOwed.map((item) => (
                <Reorder.Item key={item.id} value={item.id} className="select-none">
                  {renderPrepCommitmentRow(item, item.side)}
                </Reorder.Item>
              ))}
            </Reorder.Group>
          </div>
        ) : null}

        {profile.lastMeeting ? (
          <div className="border-t border-gray-100 pt-2">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
              <Calendar className="w-3.5 h-3.5" /> Last time
              {profile.lastMeeting.date ? (
                <span className="font-normal normal-case tracking-normal text-gray-400">
                  · {profile.lastMeeting.date}
                </span>
              ) : null}
            </div>
            {profile.lastMeeting.title ? (
              <p className="text-sm font-medium text-gray-700 break-words">{profile.lastMeeting.title}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    )
  }

  const renderPrepCardV1 = (profile) => (
    <div
      key={profile.participantId}
      className="rounded-xl border border-gray-100 bg-white/60 p-4 min-w-0"
    >
      <div className="flex items-center gap-3 mb-3 min-w-0">
        <div className="avatar text-sm shrink-0">
          {(profile.displayName ?? 'P').charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-sm font-medium text-gray-900 block truncate">
            {profile.displayName || 'Participant'}
          </span>
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span>{profile.meetingCount} meetings observed</span>
            {profile.maturityLevel === 'mature' && (
              <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                Rich profile
              </span>
            )}
          </div>
        </div>
      </div>

      {profile.aiNarrative ? (
        <p className="text-sm text-gray-700 leading-relaxed mb-3 bg-gray-50 rounded-lg p-3 break-words">
          {profile.aiNarrative}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2 mb-3">
        {profile.communicationStyle ? (
          <>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
              {VERBOSITY_LABELS[profile.communicationStyle.verbosity] ?? profile.communicationStyle.verbosity} communicator
            </span>
            {profile.communicationStyle.interruptionPattern !== 'balanced' && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                {INTERRUPT_LABELS[profile.communicationStyle.interruptionPattern] ?? profile.communicationStyle.interruptionPattern}
              </span>
            )}
            <span className={`rounded-full bg-gray-50 px-2 py-0.5 text-[11px] font-medium ${ENGAGEMENT_COLORS[profile.communicationStyle.engagementTrend] ?? 'text-gray-600'}`}>
              Engagement: {ENGAGEMENT_LABELS[profile.communicationStyle.engagementTrend] ?? profile.communicationStyle.engagementTrend}
            </span>
          </>
        ) : null}
        {profile.participationTrend ? (
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-600">
            Trend: {profile.participationTrend}
          </span>
        ) : null}
      </div>

      {profile.relationshipContext ? (
        <div className="text-xs text-gray-500 border-t border-gray-100 pt-2 space-y-0.5">
          <p>
            <span className="font-medium text-gray-700">{profile.relationshipContext.sharedMeetingCount}</span>{' '}
            shared meetings
            {profile.relationshipContext.lastSharedMeetingDate ? (
              <span>
                {' '}· Last: {profile.relationshipContext.lastSharedMeetingDate}
                {profile.relationshipContext.lastSharedMeetingTitle ? (
                  <span className="text-gray-400"> ({profile.relationshipContext.lastSharedMeetingTitle})</span>
                ) : null}
              </span>
            ) : null}
          </p>
        </div>
      ) : null}
    </div>
  )

  const renderParticipantPrepProfilesCard = () => (
    <motion.div className="glass rounded-2xl p-6 h-full flex flex-col min-w-0 min-h-[60vh]">
      <div className="flex items-start justify-between gap-2 mb-4 shrink-0">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 shrink-0 text-primary-600" />
            Prep Notes
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            What you need before this meeting: top open commitments, context, and last time you met.
          </p>
        </div>
        <button
          onClick={() => void fetchParticipantPrepProfiles()}
          disabled={participantPrepProfilesLoading}
          className="shrink-0 p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-60"
          title="Refresh prep notes"
        >
          <RefreshCw className={`w-4 h-4 ${participantPrepProfilesLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
      {participantPrepProfilesLoading ? (
        <div className="flex flex-1 items-center justify-center gap-2 py-4 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading prep notes...</span>
        </div>
      ) : participantPrepProfilesError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{participantPrepProfilesError}</p>
        </div>
      ) : participantPrepProfiles.length === 0 && prepStrategyContext.length === 0 ? (
        <div className="flex-1 rounded-xl border border-dashed border-gray-200 bg-gray-50/50 p-4 text-center flex flex-col justify-center">
          <p className="text-sm font-medium text-gray-600 mb-1">Nothing to prep yet</p>
          <p className="text-xs text-gray-400 leading-relaxed">
            Prep cards draw on shared history: open commitments, who each person is,
            and what happened last time you met. They fill in as Rithmo sits in more meetings together.
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 space-y-3 overflow-y-auto pr-1">
          {prepStrategyContext.length > 0 ? (
            <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-violet-500 mb-1.5">
                <Lightbulb className="w-3.5 h-3.5" /> Strategy context
              </div>
              <ul className="space-y-1">
                {prepStrategyContext.map((s) => (
                  <li key={s.id} className="flex items-start gap-1.5 text-sm text-gray-700">
                    {s.polarity === 'negative' ? (
                      <Ban className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
                    ) : (
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
                    )}
                    <span className="break-words">
                      {s.summary}
                      {s.theme ? <span className="ml-1.5 text-xs text-gray-400">· {s.theme}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {participantPrepProfiles.map((profile) =>
            participantPrepProfilesVersion === 1
              ? renderPrepCardV1(profile)
              : renderPrepCardV2(profile),
          )}
        </div>
      )}
      </div>
    </motion.div>
  )

  const renderDisabledLiveControlsShowcase = () => {
    const previewTopic =
      agendaStatus?.items?.[agendaStatus.currentIndex]?.title ||
      meeting?.agenda?.[0]?.title ||
      'Agenda items will become actionable once the meeting starts.'

    return (
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-2xl p-6 opacity-80">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-400" />
            Participant Controls
          </h3>
          <p className="text-sm text-gray-500">
            Participant moderation becomes available only while a meeting is actively live.
          </p>
          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="avatar text-sm opacity-60">P</div>
                <span className="text-gray-500">Participant controls locked</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDisabledLiveControlHintArea('participant')}
                  aria-disabled="true"
                  className="rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-xs font-medium text-gray-400 cursor-not-allowed"
                >
                  <span className="inline-flex items-center gap-2">
                    <MicOff className="w-4 h-4" />
                    Mute
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setDisabledLiveControlHintArea('participant')}
                  aria-disabled="true"
                  className="rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-xs font-medium text-gray-400 cursor-not-allowed"
                >
                  <span className="inline-flex items-center gap-2">
                    <Mic className="w-4 h-4" />
                    Unmute
                  </span>
                </button>
              </div>
            </div>
            {disabledLiveControlHintArea === 'participant' ? (
              <p className="mt-3 text-xs text-gray-500">
                Participant mute and unmute controls only work during a live meeting.
              </p>
            ) : null}
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass rounded-2xl p-6 opacity-80">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-gray-400" />
              Rithmo Custom Announcement
            </h3>
            <div className="space-y-3">
              <textarea
                value=""
                readOnly
                aria-disabled="true"
                placeholder="Announcements can be sent only while the meeting is live."
                rows={3}
                className="input-field resize-none cursor-not-allowed bg-gray-100 text-gray-400"
              />
              <button
                type="button"
                onClick={() => setDisabledLiveControlHintArea('announcement')}
                aria-disabled="true"
                className="w-full rounded-xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm font-medium text-gray-400 cursor-not-allowed"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <Volume2 className="w-5 h-5" />
                  Announce
                </span>
              </button>
              {disabledLiveControlHintArea === 'announcement' ? (
                <p className="text-xs text-gray-500">
                  Bot announcements are disabled until the meeting is actively in progress.
                </p>
              ) : null}
            </div>
          </div>

          <div className="glass rounded-2xl p-6 opacity-80">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-400" />
              Agenda Controls
            </h3>
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-gray-100 border border-gray-200">
                <p className="text-sm text-gray-500 mb-1">Current Item</p>
                <p className="font-semibold text-gray-500">{previewTopic}</p>
              </div>
              <button
                type="button"
                onClick={() => setDisabledLiveControlHintArea('agenda')}
                aria-disabled="true"
                className="w-full rounded-xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm font-medium text-gray-400 cursor-not-allowed"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <SkipForward className="w-5 h-5" />
                  Next Agenda Item
                </span>
              </button>
              {disabledLiveControlHintArea === 'agenda' ? (
                <p className="text-xs text-gray-500">
                  Agenda advancement is available only while a live meeting is running.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // When navigating to a different meeting (e.g. from Meeting History), reset state so we show loading and then the new meeting
  const prevMeetingIdRef = useRef(meetingId)
  useEffect(() => {
    if (prevMeetingIdRef.current !== meetingId) {
      prevMeetingIdRef.current = meetingId
      setActiveTab('overview')
      setMeeting(null)
      setSeriesInstances([])
      setSeriesInstancesLoading(false)
      setRecapOutputs([])
      setCoaching(null)
      setClosedOutputIds(new Map())
      setIsEditingAgenda(false)
      setIoEnvelope(null)
      setIoError(null)
      setLiveInsights(null)
      setLiveInsightsError(null)
      setLiveInsightsLoading(false)
      setMemberPrivateGoalsState(null)
      setMemberPrivateGoalsError(null)
      setMemberPrivateGoalsLoading(false)
      setParticipantContextRows([])
      setParticipantContextError(null)
      setParticipantContextLoading(false)
      setScrubbingParticipantContextIds(new Set())
    }
  }, [meetingId])

  useEffect(() => {
    if (activeTab !== 'agenda') return
    void fetchParticipantContextMemory()
    void fetchParticipantPrepProfiles()
    void fetchMemberPrivateGoals()
  }, [activeTab, fetchMemberPrivateGoals, fetchParticipantContextMemory, fetchParticipantPrepProfiles])

  useEffect(() => {
    if (activeTab === 'pre-read') setActiveTab('agenda')
  }, [activeTab])

  useEffect(() => {
    if (activeTab === 'participants') setActiveTab('overview')
  }, [activeTab])

  useEffect(() => {
    if (!meetingId || activeTab !== 'controls') {
      return
    }

    void fetchLiveInsights()
    void fetchParticipantPrepProfiles()
    void fetchParticipantContextMemory()
    void fetchMemberPrivateGoals()
  }, [activeTab, fetchLiveInsights, fetchParticipantPrepProfiles, fetchParticipantContextMemory, fetchMemberPrivateGoals, meetingId])

  // Deep-link: ?tab=controls opens Live Meeting Insights (e.g. dashboard live banner);
  // ?tab=agenda opens Prep (e.g. dashboard "Today" meeting cards).
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab')
    if (tab === MEETING_LIVE_INSIGHTS_TAB) {
      setActiveTab(MEETING_LIVE_INSIGHTS_TAB)
    } else if (tab === 'agenda') {
      setActiveTab('agenda')
    }
  }, [location.search])

  // Auto-switch to Live Controls tab when the meeting becomes live.
  // Uses a ref to fire only on the transition (false→true), not on every render.
  const prevIsLiveRef = useRef(isLive)
  useEffect(() => {
    if (isLive && !prevIsLiveRef.current) {
      setActiveTab(MEETING_LIVE_INSIGHTS_TAB)
    }
    prevIsLiveRef.current = isLive
  }, [isLive])

  useEffect(() => {
    if (isLive || activeTab !== 'controls') {
      setDisabledLiveControlHintArea(null)
    }
  }, [activeTab, isLive, meetingId])

  // Fetch agenda status (backend returns currentItemIndex + agenda; normalize to currentIndex + items)
  const fetchAgendaStatus = useCallback(async () => {
    if (!isLive) return
    try {
      const response = await agendaAPI.getStatus(meetingId)
      const data = response?.data ?? response
      if (data != null) {
        setAgendaStatus({
          currentIndex: data.currentItemIndex ?? data.currentIndex ?? -1,
          items: data.agenda ?? data.items ?? [],
        })
      }
    } catch {
      // Agenda status optional when live
    }
  }, [meetingId, isLive])

  /**
   * Agenda tab list source: GET /meetings/:id returns DB agenda; live status returns in-memory engine items.
   * After “apply prep suggestions”, new rows exist only in the DB until the runtime agenda is refreshed,
   * so when the DB list is longer we prefer it so appended items appear.
   */
  const agendaItemsForPublicAgendaTab = useMemo(() => {
    const db = Array.isArray(meeting?.agenda) ? meeting.agenda : []
    const live = Array.isArray(agendaStatus?.items) ? agendaStatus.items : []
    if (isLive && live.length > 0 && db.length > live.length) return db
    if (isLive && live.length > 0) return live
    return db
  }, [isLive, meeting?.agenda, agendaStatus?.items])

  // Fetch bot status
  const fetchBotStatus = useCallback(async () => {
    try {
      const response = await botAPI.getStatus(meetingId)
      const data = response?.data ?? response
      // #region agent log
      fetch('http://127.0.0.1:7730/ingest/40704d02-ed97-4c5f-9aec-31ab7628389e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'ec36fc'},body:JSON.stringify({sessionId:'ec36fc',location:'MeetingDetailPage.jsx:fetchBotStatus',message:'bot getStatus response',data:{meetingId:String(meetingId),status:data?.status,connected:data?.connected,keys:data&&typeof data==='object'?Object.keys(data):[]},timestamp:Date.now(),hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
      if (data != null) setBotStatus(data)
    } catch {
      // Bot status optional
    }
  }, [meetingId])

  // #region agent log
  useEffect(() => {
    if (!isLive || !botStatus) return
    fetch('http://127.0.0.1:7730/ingest/40704d02-ed97-4c5f-9aec-31ab7628389e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'ec36fc'},body:JSON.stringify({sessionId:'ec36fc',location:'MeetingDetailPage.jsx:botStatusEffect',message:'live banner botStatus snapshot',data:{status:botStatus.status,connected:botStatus.connected},timestamp:Date.now(),hypothesisId:'H3'})}).catch(()=>{})
  }, [isLive, botStatus])
  // #endregion

  // Fetch participants from C++ bot (only when meeting is live - bot must be in meeting)
  // Also derives bot status: success = bot in call, error = bot left/not joined.
  // Teams meetings have no C++ Zoom SDK bot — calling getParticipants there
  // would always 404 and incorrectly flip botStatus.status to 'disconnected',
  // hiding the lobby banner. Skip the call entirely for Teams; the Recall
  // webhook drives botStatus via fetchBotStatus.
  const fetchParticipants = useCallback(async () => {
    if (!isLive) return
    if (meeting?.provider === 'teams' || meeting?.provider === 'meet') return
    try {
      const response = await cppBotAPI.getParticipants(meetingId)
      const list = response?.participants ?? response?.data?.participants ?? []
      if (Array.isArray(list)) {
        setParticipants((prev) => mergeParticipants(prev, list))
        // #region agent log
        fetch('http://127.0.0.1:7730/ingest/40704d02-ed97-4c5f-9aec-31ab7628389e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'ec36fc'},body:JSON.stringify({sessionId:'ec36fc',location:'MeetingDetailPage.jsx:fetchParticipants',message:'cpp participants ok, forcing status in_call_recording',data:{meetingId:String(meetingId),participantCount:list.length},timestamp:Date.now(),hypothesisId:'H2'})}).catch(()=>{});
        // #endregion
        setBotStatus((prev) => ({ ...prev, status: 'in_call_recording' }))
      }
    } catch {
      // Bot unreachable — it has left or hasn't joined yet
      // #region agent log
      fetch('http://127.0.0.1:7730/ingest/40704d02-ed97-4c5f-9aec-31ab7628389e',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'ec36fc'},body:JSON.stringify({sessionId:'ec36fc',location:'MeetingDetailPage.jsx:fetchParticipants',message:'cpp participants fetch failed',data:{meetingId:String(meetingId)},timestamp:Date.now(),hypothesisId:'H2'})}).catch(()=>{});
      // #endregion
      setBotStatus((prev) => prev?.status === 'in_call_recording'
        ? { ...prev, status: 'disconnected' }
        : prev
      )
    }
  }, [meetingId, isLive, meeting?.provider]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Load outputs from in-memory Zoom state (same source as WS) when agenda is done but meeting still live */
  const fetchLiveOutputsFromZoom = useCallback(async () => {
    if (!isLive || !meetingId) return
    try {
      const res = await zoomAPI.getState(meetingId)
      const data = res?.data ?? res
      const flat = flattenOutputsByAgendaItem(data?.outputsByAgendaItem, meetingId)
      if (flat.length > 0) setRecapOutputs(flat)
    } catch {
      /* optional */
    }
  }, [isLive, meetingId])

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await fetchMeetingData()
      await fetchBotStatus()
      setLoading(false)
    }
    loadData()
  }, [fetchMeetingData, fetchBotStatus])

  // Poll for status changes when meeting is 'created' — detects server-side transitions
  // (e.g. bots join via zoomController and set status to 'starting')
  useEffect(() => {
    if (meeting?.status !== 'created' || isLive) return
    const poll = setInterval(() => { void fetchMeetingData() }, 5000)
    return () => clearInterval(poll)
  }, [meeting?.status, isLive, fetchMeetingData])

  const refreshInternalOperator = useCallback(async () => {
    if (!meetingId || !isMeetingOrganizer) return
    try {
      const env = await fetchInternalOperatorBootstrap(meetingId)
      setIoEnvelope(env)
      setIoError(null)
    } catch (e) {
      setIoError(e instanceof Error ? e.message : 'Internal Goal Tracker refresh failed')
    }
  }, [isMeetingOrganizer, meetingId])

  /** Bootstrap INTERNAL_OPERATOR_REALTIME_V1 when live (HTTP; same contract as WS). */
  useEffect(() => {
    if (!isLive || !meetingId || !isMeetingOrganizer) {
      setIoEnvelope(null)
      setIoError(null)
      setIoLoading(false)
      return
    }
    let cancelled = false
    setIoLoading(true)
    setIoError(null)
    fetchInternalOperatorBootstrap(meetingId)
      .then((env) => {
        if (!cancelled) setIoEnvelope(env)
      })
      .catch((e) => {
        if (!cancelled) {
          setIoError(e instanceof Error ? e.message : 'Failed to load Internal Goal Tracker')
          setIoEnvelope(null)
        }
      })
      .finally(() => {
        if (!cancelled) setIoLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isLive, isMeetingOrganizer, meetingId])

  // WebSocket connection for live meeting state push
  useEffect(() => {
    if (!isLive || !meetingId) return

    const origin = getServerOrigin()
    const token =
      typeof localStorage !== 'undefined' ? localStorage.getItem('rithmo_token') : null
    let wsUrl =
      origin.replace(/^http/, 'ws') + `/ws/moderation?meetingId=${encodeURIComponent(meetingId)}`
    if (token) {
      wsUrl += `&access_token=${encodeURIComponent(token)}`
    }

    let ws
    let reconnectTimer
    const connect = () => {
      ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        setWsConnected(true)
      }

      ws.onmessage = (event) => {
        try {
          const io = parseInternalOperatorRealtimeWsMessage(event.data)
          if (io) {
            setIoEnvelope(io)
            setIoError(null)
            return
          }
          const liveInsightsEnvelope = parseLiveMeetingInsightsRealtimeWsMessage(event.data)
          if (liveInsightsEnvelope) {
            setLiveInsights(liveInsightsEnvelope.insights)
            setLiveInsightsError(null)
            setLiveInsightsLoading(false)
            return
          }
          const msg = JSON.parse(event.data)
          if (msg.type === 'MEETING_STATE_UPDATE' && msg.payload) {
            const p = msg.payload

            // --- Agenda ---
            // WS shape: { agenda: { items: [...], currentIndex } }
            // Normalize state-engine field names to what the dashboard renders
            if (p.agenda) {
              const rawItems = p.agenda.items ?? p.agenda.agenda ?? []
              const items = rawItems.map((item) => ({
                ...item,
                id: item.id ?? item.agendaItemId,
                title: item.title,
                durationSec: item.durationSec ?? item.plannedDurationSec ?? item.timeboxSec,
                duration: item.duration ?? (item.plannedDurationSec ? Math.round(item.plannedDurationSec / 60) : undefined),
              }))
              setAgendaStatus({
                currentIndex: p.agenda.currentItemIndex ?? p.agenda.currentIndex ?? -1,
                items,
              })
            }

            // --- Participants ---
            if (Array.isArray(p.participants) && p.participants.length > 0) {
              setParticipants((prev) => mergeParticipants(prev, p.participants))
            }

            // --- Lifecycle / meeting status ---
            if (p.lifecycle) {
              const wsStatus = p.lifecycle.status
              if (wsStatus) {
                // Normalize state-engine status to dashboard status
                // State engine: 'live' / 'ended' / 'pending'
                // Dashboard expects: 'live' / 'completed' / 'created' / 'in-progress' / 'active'
                const normalizedStatus = wsStatus === 'ended' ? 'completed' : wsStatus
                // Do NOT let the WS push downgrade from 'starting' back to 'created'.
                // The DB is source of truth for 'starting' (bots joined); the state engine
                // still shows 'created'/'pending' until auto-start fires engines.startMeeting.
                const isWsDowngrade = normalizedStatus === 'created' || normalizedStatus === 'pending'
                setMeeting((prev) => {
                  if (!prev) return prev
                  const prevStartingOrLive = prev.status === 'starting' || prev.status === 'live'
                  if (prevStartingOrLive && isWsDowngrade) return prev
                  return { ...prev, status: normalizedStatus }
                })
                if (!isWsDowngrade) {
                  const nowLive = wsStatus === 'in-progress' || wsStatus === 'active' || wsStatus === 'live' || wsStatus === 'starting'
                  setIsLive(nowLive)
                }
                // Meeting ended → bot is gone
                if (wsStatus === 'ended') {
                  setIsLive(false)
                  setBotStatus((prev) => prev ? { ...prev, status: 'disconnected' } : prev)
                }
              }
            }

            // --- Outputs (live meeting, incl. after last agenda item — enables dashboard edits) ---
            if (p.outputsByAgendaItem && typeof p.outputsByAgendaItem === 'object') {
              const flat = flattenOutputsByAgendaItem(p.outputsByAgendaItem, meetingId)
              if (flat.length > 0) setRecapOutputs(flat)
            }
          }
        } catch { /* ignore parse errors */ }
      }

      ws.onclose = () => {
        wsRef.current = null
        setWsConnected(false)
        // Reconnect after 3s
        reconnectTimer = setTimeout(connect, 3000)
      }

      ws.onerror = () => {
        // onclose will fire after this
      }
    }

    connect()

    return () => {
      clearTimeout(reconnectTimer)
      setWsConnected(false)
      if (ws && ws.readyState <= WebSocket.OPEN) {
        ws.close()
      }
      wsRef.current = null
    }
  }, [isLive, meetingId])

  // After all agenda items (currentIndex -1) while still live: poll Zoom state for outputs so edits work before meeting end
  useEffect(() => {
    if (!isLive || !meetingId) return
    const items = agendaStatus?.items ?? []
    const cur = agendaStatus?.currentIndex ?? -1
    const agendaComplete = items.length > 0 && cur < 0
    if (!agendaComplete) return
    void fetchLiveOutputsFromZoom()
    const id = setInterval(() => {
      void fetchLiveOutputsFromZoom()
    }, 30000)
    return () => clearInterval(id)
  }, [isLive, meetingId, agendaStatus, fetchLiveOutputsFromZoom])

  // Poll for updates when live
  // When WS is connected: agenda comes via push, only poll C++ bot participants (every 30s)
  // When WS is disconnected: poll agenda + participants + live insights every 5s
  useEffect(() => {
    if (!isLive) return

    if (wsConnected) {
      // WS handles agenda + lifecycle. Only poll C++ bot for participants.
      fetchParticipants()
      const pollInterval = setInterval(fetchParticipants, 30000)
      return () => clearInterval(pollInterval)
    }

    // No WS — fast poll everything including live insights
    fetchAgendaStatus()
    fetchParticipants()
    void fetchLiveInsights()
    const pollInterval = setInterval(() => {
      fetchAgendaStatus()
      fetchParticipants()
      void fetchLiveInsights()
    }, 5000)
    return () => clearInterval(pollInterval)
  }, [isLive, wsConnected, fetchAgendaStatus, fetchParticipants, fetchLiveInsights])

  // Start/Join meeting when status is created (user navigated from dashboard/upcoming list)
  // For recurring: always create child occurrence first (1st run or next), then start that and navigate
  const [startingMeeting, setStartingMeeting] = useState(false)
  // Concurrent-Zoom-call confirmation modal. The Start handler awaits a Promise resolved by
  // the modal's Cancel/Continue buttons, so the existing async flow doesn't change shape.
  // End-meeting confirmation modal (replaces window.confirm)
  const [endMeetingConfirm, setEndMeetingConfirm] = useState(null)
  const endMeetingConfirmResolverRef = useRef(null)
  const askEndMeetingConfirm = useCallback((label) => {
    return new Promise((resolve) => {
      endMeetingConfirmResolverRef.current = resolve
      setEndMeetingConfirm({ open: true, label })
    })
  }, [])
  const resolveEndMeetingConfirm = useCallback((value) => {
    const fn = endMeetingConfirmResolverRef.current
    endMeetingConfirmResolverRef.current = null
    setEndMeetingConfirm(null)
    if (fn) fn(value)
  }, [])

  const [concurrentConfirm, setConcurrentConfirm] = useState(null)
  const concurrentConfirmResolverRef = useRef(null)
  const askConcurrentZoomConfirm = useCallback(() => {
    return new Promise((resolve) => {
      concurrentConfirmResolverRef.current = resolve
      setConcurrentConfirm({ open: true })
    })
  }, [])
  const resolveConcurrentConfirm = useCallback((value) => {
    const fn = concurrentConfirmResolverRef.current
    concurrentConfirmResolverRef.current = null
    setConcurrentConfirm(null)
    if (fn) fn(value)
  }, [])
  // Other live meeting owned by this user (different from the current one) — blocks starting
  const [otherActiveMeeting, setOtherActiveMeeting] = useState(null)

  useEffect(() => {
    let active = true
    dashboardAPI.getActiveMeeting().then((res) => {
      if (!active) return
      const m = res?.data?.activeMeeting ?? res?.activeMeeting ?? null
      // Only block if the active meeting is a *different* one
      if (m && String(m.id) !== String(meetingId)) {
        setOtherActiveMeeting(m)
      } else {
        setOtherActiveMeeting(null)
      }
    }).catch(() => {
      if (active) setOtherActiveMeeting(null)
    })
    return () => { active = false }
  }, [meetingId])

  const handleStartMeeting = async () => {
    // Block if billing enforcement is on and org can't start meetings
    if (billing.enforced && !billing.canStartMeeting) {
      toast.error(
        'Plan required',
        (!billing.plan || billing.plan.planKey === 'free')
          ? 'Your organization needs a plan to start meetings.'
          : 'Monthly meeting hours exhausted. Upgrade your plan.'
      )
      return
    }

    const isRecurringStart =
      meeting?.isRecurring && (meeting?.status === 'created' || meeting?.status === 'ended')
    let effectiveMeetingId = meetingId
    let joinUrl = meeting?.joinUrl || meeting?.meetingUrl || addBotJoinUrl
    let password = meeting?.password || addBotPassword

    if (isRecurringStart) {
      try {
        const nextRes = await meetingsAPI.startNextOccurrence(meetingId)
        effectiveMeetingId = nextRes?.data?.meetingId ?? nextRes?.meetingId ?? meetingId
        if (nextRes?.data?.joinUrl ?? nextRes?.joinUrl) joinUrl = nextRes?.data?.joinUrl ?? nextRes?.joinUrl
        if ((nextRes?.data?.password ?? nextRes?.password) != null) password = nextRes?.data?.password ?? nextRes?.password
      } catch (err) {
        toast.error(err?.message ?? 'Could not create occurrence.')
        return
      }
    }

    if (!joinUrl) {
      toast.error('No join link', 'This meeting has no join URL yet.')
      return
    }

    const isOrganizer = Boolean(
      user?.id && (meeting?.userId ?? meeting?.ownerId) && String(user.id) === String(meeting.userId ?? meeting.ownerId)
    )

    setStartingMeeting(true)

    // Persist `starting` before Zoom bot join: blocks concurrent starts (GET /dashboard/active-meeting)
    // and avoids marking the meeting `live` until the start-existing handshake succeeds.
    // Do not call Zoom until this succeeds — otherwise the DB never reserves `starting` and races persist.
    if (isOrganizer) {
      try {
        await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'starting' })
        setMeeting((prev) => (prev ? { ...prev, status: 'starting' } : prev))
      } catch (e) {
        toast.error(
          e?.message ?? 'Could not reserve meeting start',
          'Please try again. If this keeps happening, refresh the page.'
        )
        setStartingMeeting(false)
        return
      }
    }

    const revertStartingStatus = async () => {
      if (!isOrganizer) return
      try {
        await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'created' })
        setMeeting((prev) => (prev ? { ...prev, status: 'created' } : prev))
      } catch (e) {
        console.warn('Could not revert meeting status to created after failed start', e)
      }
    }

    /** Set true after DB `live` — outer catch must not revert a successful start. */
    let advancedStatusToLive = false

    try {
      if (meeting?.provider === 'zoom') {
        let zoomOk = false
        // Try without `force` first. If the backend detects another live Zoom call on
        // this account it returns 409 CONCURRENT_ZOOM_CALL — confirm with the user
        // before retrying with `force: true`, which ends the other call.
        const callStart = (force) =>
          zoomAPI.startExistingMeetingForUser({
            meetingId: effectiveMeetingId,
            password,
            force,
          })
        try {
          let res
          try {
            res = await callStart(false)
          } catch (firstErr) {
            if (firstErr?.status === 409 && firstErr?.code === 'CONCURRENT_ZOOM_CALL') {
              const confirmed = await askConcurrentZoomConfirm()
              if (!confirmed) {
                if (isRecurringStart && effectiveMeetingId !== meetingId) {
                  try {
                    await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'cancelled' })
                  } catch (cleanupErr) {
                    console.warn('Could not cancel orphaned occurrence', cleanupErr)
                  }
                } else {
                  await revertStartingStatus()
                }
                return
              }
              res = await callStart(true)
            } else if (firstErr?.status === 409 && firstErr?.code === 'CONCURRENT_ZOOM_CALL_DETECTED') {
              // The bot tried but got stuck in CONNECTING — surfaced after the fact because
              // the proactive detection couldn't run (missing OAuth scope or non-host meeting).
              if (isRecurringStart && effectiveMeetingId !== meetingId) {
                try {
                  await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'cancelled' })
                } catch (cleanupErr) {
                  console.warn('Could not cancel orphaned occurrence', cleanupErr)
                }
              } else {
                await revertStartingStatus()
              }
              toast.error(
                'Already in another Zoom call',
                'Please leave your other Zoom meeting and try again.'
              )
              return
            } else {
              throw firstErr
            }
          }
          if (res?.success) {
            toast.success(res?.message ?? 'Meeting started; opening join link.')
            setCppBotJoined(true)
            zoomOk = true
          }
        } catch (zoomErr) {
          // Zoom start failed — if we created a child occurrence, cancel it so the
          // parent doesn't get stuck showing "meeting in progress".
          if (isRecurringStart && effectiveMeetingId !== meetingId) {
            try {
              await meetingsAPI.updateStatus(effectiveMeetingId, { status: 'cancelled' })
            } catch (cleanupErr) {
              console.warn('Could not cancel orphaned occurrence', cleanupErr)
            }
          } else {
            await revertStartingStatus()
          }
          toast.error(
            zoomErr?.message ?? 'Zoom is not connected. Connect Zoom from Settings and try again.'
          )
          return
        }
        if (!zoomOk) {
          await revertStartingStatus()
          return
        }
      }

      // Backend startExistingMeetingForUser already set DB status to 'starting'.
      // The lifecycle coordinator will transition to 'live' when auto-start fires.
      // Update local state to 'starting' so the UI shows live controls immediately.
      setMeeting((prev) => (prev ? { ...prev, status: 'starting' } : prev))
      advancedStatusToLive = true
      setIsLive(true)

      // Launch Zoom via a tiny throwaway popup that fires the desktop app and
      // then closes itself, instead of a full tab that drags the user off the
      // dashboard. Must run inside this click gesture so the popup isn't blocked.
      // Non-Zoom providers (Teams/Meet) keep the plain open-in-tab behaviour.
      if (meeting?.provider === 'zoom') {
        launchZoomMeeting(joinUrl)
      } else {
        window.open(joinUrl, '_blank')
      }

      // Anchor the dashboard tab on Live Insights so the user lands there the
      // moment they come back from Zoom — they no longer have to remember it
      // exists. `replace` so Back doesn't bounce through the pre-start view.
      const liveInsightsPath = getMeetingLiveInsightsPath(effectiveMeetingId)
      if (liveInsightsPath) {
        navigate(liveInsightsPath, { replace: true })
      } else if (isRecurringStart && effectiveMeetingId !== meetingId) {
        navigate(`/meeting/${effectiveMeetingId}`, { replace: true })
      }
    } catch (err) {
      if (!advancedStatusToLive) {
        await revertStartingStatus()
      }
      toast.error(err?.message ?? 'Could not start meeting. You can still join via the link below.')
    } finally {
      setStartingMeeting(false)
    }
  }

  const isCompleted = meeting?.status === 'completed' || meeting?.status === 'ended'
  const isScheduled = meeting?.status === 'scheduled'
  const isParent = Boolean(meeting?.isRecurring && !meeting?.parentMeetingId)
  const isChild = Boolean(meeting?.parentMeetingId)
  const isOneOff = Boolean(!meeting?.isRecurring && !meeting?.parentMeetingId)
  const liveInsightsSourceMeetingId =
    typeof liveInsights?.sourceMeetingId === 'string' && liveInsights.sourceMeetingId.trim().length > 0
      ? liveInsights.sourceMeetingId
      : null
  const isLiveInsightsProxyingActiveOccurrence = Boolean(
    !isLive &&
      isParent &&
      liveInsights?.dataMode === 'live' &&
      liveInsightsSourceMeetingId &&
      liveInsightsSourceMeetingId !== meetingId
  )
  const canRequestLiveInsights = Boolean(isLive || meeting?.status === 'ended' || isParent)
  const canEditAgenda = !meeting?.isImported && (isParent || (isOneOff && (meeting?.status === 'created' || meeting?.status === 'scheduled')))
  const hasActiveChild =
    meeting?.hasActiveChild ||
    (isParent &&
      seriesInstances.some((inst) =>
        ['live', 'starting', 'paused', 'ending'].includes(inst.status || '')
      ))

  const isZoomMeeting = meeting?.provider === 'zoom'
  const isTeamsMeeting = meeting?.provider === 'teams'
  const isMeetMeeting = meeting?.provider === 'meet'
  const isSlackMeeting = meeting?.provider === 'slack'
  // Convenience for lobby / admit / failure / end-meeting UX that's identical
  // across the two external Recall-driven providers (Teams + Meet). When a
  // branch is genuinely Teams- or Meet-specific (lobby wording, badge color),
  // use isTeamsMeeting / isMeetMeeting directly.
  const isExternalMeeting = isTeamsMeeting || isMeetMeeting
  const zoomReady = !isZoomMeeting || zoomConnected
  const isAssistantExternal = meeting?.meetingJoinKind === 'assistant_external'
  // External Recall meetings (Teams + Meet) get their status purely from
  // Recall's bot.status_change webhook (relayed via botAPI.getStatus). Don't
  // call cppBotAPI.getParticipants for them — no C++ Zoom SDK bot exists, the
  // call would just fail and flip botStatus.status to 'disconnected', breaking
  // the lobby banner UX.
  const botStatusCode = botStatus?.status ?? null
  const isBotInLobby = isExternalMeeting && botStatusCode === 'in_waiting_room'
  const isBotTranscribingLive = isExternalMeeting && (botStatusCode === 'in_call_recording' || botStatusCode === 'in_call_not_recording')
  // Failure states from Recall: failed_to_join (host denied admission OR
  // meeting URL invalid/expired) and fatal (Recall-side error). We surface a
  // clear remediation banner so the user isn't stuck wondering why the bot
  // never appeared.
  const isBotFailedToJoin = isExternalMeeting && botStatusCode === 'failed_to_join'
  const isBotFatal = isExternalMeeting && botStatusCode === 'fatal'

  const canStartMeeting =
    !meeting?.isImported &&
    !isChild &&
    !hasActiveChild &&
    !otherActiveMeeting &&
    (isAssistantExternal || zoomReady) &&
    (meeting?.status === 'created' || (meeting?.isRecurring && meeting?.status === 'ended')) &&
    (meeting?.joinUrl || meeting?.meetingUrl || addBotJoinUrl)
  const isStartNextOccurrence = meeting?.isRecurring && meeting?.status === 'ended'
  const showStartBlockedZoomUi =
    !canStartMeeting &&
    isZoomMeeting &&
    !zoomConnected &&
    !hasActiveChild &&
    !otherActiveMeeting &&
    (meeting?.status === 'created' || (meeting?.isRecurring && meeting?.status === 'ended'))
  const showStartBlockedOtherActiveUi =
    !canStartMeeting &&
    Boolean(otherActiveMeeting) &&
    (meeting?.status === 'created' || (meeting?.isRecurring && meeting?.status === 'ended'))
  const showStartBlockedSubtextRow = showStartBlockedZoomUi || showStartBlockedOtherActiveUi

  // Only show "Add bot" CTA when we can positively detect the bot is NOT connected.
  // If botStatus is null/unknown, we hide the CTA to avoid unnecessary noise.
  const botAutoJoinFailed = Boolean(isLive && botStatus && botStatus.connected === false)
  const nonLiveControlsBannerText = isLiveInsightsProxyingActiveOccurrence
    ? 'This recurring parent is showing live insights from the active occurrence.'
    : isParent
    ? 'Live Meeting Insights are disabled until a recurring occurrence is actively in progress. Review the most recent saved state below.'
    : meeting?.status === 'ended'
      ? 'Live Meeting Insights are disabled because this meeting is no longer in progress. You can review the final saved state below.'
      : 'Live Meeting Insights will enable once the meeting is in progress.'

  useEffect(() => {
    if ((meeting?.audienceMode ?? 'internal') !== 'external') setAgendaLens('public')
  }, [meeting?.audienceMode])

  // Child meetings don't have Meeting History tab; switch to overview if we're on history
  useEffect(() => {
    if (isChild && activeTab === 'history') {
      setActiveTab('overview')
    }
  }, [isChild, activeTab])

  // Recurring parent: Analytics-style scorecard for ended occurrences only (scoped via API)
  useEffect(() => {
    const recurringParent = Boolean(meeting?.isRecurring && !meeting?.parentMeetingId)
    if (!recurringParent || !meetingId) {
      setSeriesAnalytics(null)
      setSeriesAnalyticsLoading(false)
      return
    }
    let cancelled = false
    const period = SERIES_SCORECARD_PERIOD_MAP[seriesAnalyticsTimeframe] || '30d'
    setSeriesAnalyticsLoading(true)
    analyticsAPI
      .getDashboard(period, null, meetingId)
      .then((res) => {
        if (!cancelled) setSeriesAnalytics(res?.data ?? null)
      })
      .catch((e) => {
        console.warn('Could not load series scorecard analytics', e)
        if (!cancelled) setSeriesAnalytics(null)
      })
      .finally(() => {
        if (!cancelled) setSeriesAnalyticsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [meeting?.isRecurring, meeting?.parentMeetingId, meetingId, seriesAnalyticsTimeframe])

  // Sync editable agenda from meeting when an editable meeting loads
  useEffect(() => {
    if (meeting?.agenda && canEditAgenda) {
      setEditableAgenda(meeting.agenda.map((a, i) => ({
        id: a.id ?? `item_${i}`,
        title: a.title ?? a.name ?? '',
        description: a.description ?? a.details ?? '',
        durationSec: a.durationSec ?? (a.duration ? a.duration * 60 : 300),
      })))
    } else {
      setEditableAgenda([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting?.agenda, meeting?.isRecurring, meeting?.parentMeetingId, meeting?.status])

  const handleCancelAgendaEdit = () => {
    if (meeting?.agenda) {
      setEditableAgenda(meeting.agenda.map((a, i) => ({
        id: a.id ?? `item_${i}`,
        title: a.title ?? a.name ?? '',
        description: a.description ?? a.details ?? '',
        durationSec: a.durationSec ?? (a.duration ? a.duration * 60 : 300),
      })))
    }
    setIsEditingAgenda(false)
    toast.info('Changes reverted', 'Your edits have been discarded.')
  }

  const handleSaveAgenda = async () => {
    if (!meeting?.id || !canEditAgenda) return
    setAgendaSaving(true)
    try {
      const agendaPayload = editableAgenda.map((a, i) => ({
        id: a.id,
        title: a.title,
        description: a.description || undefined,
        durationSec: a.durationSec ?? 300,
        order: i,
      }))
      await meetingsAPI.update(meeting.id, { agenda: agendaPayload })
      setMeeting((prev) => prev ? { ...prev, agenda: agendaPayload } : prev)
      setIsEditingAgenda(false)
      toast.success('Agenda saved', 'Your agenda has been updated.')
    } catch {
      toast.error('Save failed', 'Could not save the agenda. Please try again.')
    } finally {
      setAgendaSaving(false)
    }
  }

  const handleSaveAndSendUpdate = async () => {
    if (!meeting?.id || !canEditAgenda) return
    setAgendaSaving(true)
    try {
      const agendaPayload = editableAgenda.map((a, i) => ({
        id: a.id,
        title: a.title,
        description: a.description || undefined,
        durationSec: a.durationSec ?? 300,
        order: i,
      }))
      const participantsPayload = (meeting.participants ?? []).map((p) => ({
        id: p.participantId ?? p.id,
        email: p.email ?? '',
        name: p.displayName ?? p.name ?? '',
        displayName: p.displayName ?? p.name ?? '',
      }))
      const governanceConfig = meeting.governanceSettings ? {
        talkEquity: { enabled: meeting.governanceSettings.talkEquity?.enabled !== false },
        interruptionPrevention: { enabled: meeting.governanceSettings.interruptionPrevention?.enabled !== false },
        agendaEnforcement: { enabled: meeting.governanceSettings.agendaEnforcement?.enabled !== false },
        requireOutputOwnership: meeting.governanceSettings.requireOutputOwnership === true,
      } : undefined
      await meetingsAPI.sendUpdate(meeting.id, {
        title: meeting.title,
        description: meeting.description,
        agenda: agendaPayload,
        participants: participantsPayload,
        governanceConfig,
        scheduledStartTime: meeting.scheduledStartTime ?? meeting.startTime,
        scheduledEndTime: meeting.scheduledEndTime ?? meeting.endTime,
        ...(isParent ? {
          isRecurring: true,
          recurringSeriesId: meeting.recurringSeriesId,
          recurrenceRule: meeting.recurrenceRule,
        } : {}),
      })
      setMeeting((prev) => prev ? { ...prev, agenda: agendaPayload } : prev)
      setIsEditingAgenda(false)
      toast.success('Update sent', 'Agenda saved and participants notified.')
    } catch {
      toast.error('Send update failed', 'Could not save or send. Please try again.')
    } finally {
      setAgendaSaving(false)
    }
  }

  // Share report: build plain-text report from meeting + outputs + scorecard
  const buildReportText = useCallback(() => {
    if (!meeting) return ''
    const title = decodeHtmlEntities(meeting.title || 'Meeting Report')
    const date = meeting.startTime
      ? format(parseISO(meeting.startTime), 'EEEE, MMMM d, yyyy')
      : meeting.startedAtMs
        ? format(new Date(meeting.startedAtMs), 'EEEE, MMMM d, yyyy')
        : '—'
    const scheduledStart = meeting.scheduledStartTime
      ? format(parseISO(meeting.scheduledStartTime), 'h:mm a')
      : meeting.startedAtMs
        ? format(new Date(meeting.startedAtMs), 'h:mm a')
        : '—'
    const scheduledEnd = meeting.scheduledEndTime
      ? format(parseISO(meeting.scheduledEndTime), 'h:mm a')
      : meeting.endedAtMs
        ? format(new Date(meeting.endedAtMs), 'h:mm a')
        : '—'
    const timeRange = `${scheduledStart} – ${scheduledEnd}`
    const participantList =
      meeting.participants?.length > 0
        ? meeting.participants.map((p) => p.displayName || p.name || p.email || '—').join(', ')
        : '—'
    const recurrence = meeting.recurrenceRule?.end_times
      ? `Repeats ${meeting.recurrenceRule.end_times} times`
      : meeting.isRecurring
        ? 'Recurring'
        : ''
    const scorecardLabels = {
      'talk-equity': 'Talk Equity',
      'agenda-adherence': 'Agenda Adherence',
      interruptions: 'Conversation Flow',
      'decision-velocity': 'Output Velocity',
      'time-discipline': 'Time Discipline',
    }
    let scorecardSection = ''
    if (meeting.scorecard && typeof meeting.scorecard === 'object') {
      const lines = SCORECARD_DIMENSION_IDS.map((k) => {
        const v = meeting.scorecard[k]
        const disp =
          typeof v === 'number' && Number.isFinite(v) ? `${v}/100` : 'Not available'
        return `  ${scorecardLabels[k] ?? k}: ${disp}`
      })
      const totalDisp = overallScoreFromMeetingScorecard(meeting.scorecard)
      scorecardSection =
        'Meeting Scorecard\n' +
        lines.join('\n') +
        (totalDisp != null ? `\n  Total Score: ${totalDisp}/100\n` : '\n  Total Score: —\n')
    }
    const categoryLabel = (o) => {
      const t = (o?.type || '').toLowerCase().replace(/-/g, '_')
      if (t === 'decision') {
        const v = o?.strategy?.verdict
        return v === 'operational' || v === 'noise' ? 'Other decisions' : 'Strategy'
      }
      if (t === 'action_item') return 'Action Items'
      if (t === 'follow_up') return 'Follow Ups'
      return 'Other'
    }
    const byCategory = {}
    ;(recapOutputs || []).forEach((o) => {
      const label = categoryLabel(o)
      if (!byCategory[label]) byCategory[label] = []
      byCategory[label].push(o)
    })
    const order = ['Strategy', 'Action Items', 'Follow Ups', 'Other decisions', 'Other']
    const outputsSection =
      'Meeting Outputs\n' +
      order
        .filter((label) => byCategory[label]?.length)
        .flatMap((label) =>
          (byCategory[label] || []).map(
            (o) => `  • ${label}: ${decodeHtmlEntities(String(o.summary ?? '').trim() || '—')}${o.owner ? ` (${decodeHtmlEntities(o.owner)})` : ''}`
          )
        )
        .join('\n')
    const lines = [
      title,
      '',
      'Date: ' + date,
      'Time: ' + timeRange,
      'Participants: ' + participantList,
      recurrence ? 'Recurrence: ' + recurrence : '',
      '',
      scorecardSection,
      outputsSection,
    ].filter(Boolean)
    return lines.join('\n')
  }, [meeting, recapOutputs])

  const handleShareCopy = async () => {
    const text = buildReportText()
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Report copied', 'Meeting report copied to clipboard.')
      setShowShareMenu(false)
    } catch {
      toast.error('Copy failed', 'Could not copy to clipboard.')
    }
  }

  const handleShareDownload = () => {
    const text = buildReportText()
    const filename = `Meeting-Report-${(meeting?.title || 'Report').replace(/[^a-zA-Z0-9-_]/g, '-')}-${format(new Date(), 'yyyy-MM-dd')}.txt`
    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Report downloaded', filename)
    setShowShareMenu(false)
  }

  const handleTranscriptDownload = async (format = 'txt') => {
    const ext = format === 'pdf' ? 'pdf' : format === 'jsonl' ? 'jsonl' : 'txt'
    const filename = `transcript-${meetingId}.${ext}`
    try {
      const blob = await meetingsAPI.getTranscript(meetingId, format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Transcript downloaded', filename)
    } catch (err) {
      toast.error(err?.message ?? 'Transcript not available for this meeting.')
    }
    setShowShareMenu(false)
  }

  const handleShareEmailOpen = () => {
    setShowShareMenu(false)
    setShowEmailReportModal(true)
    setShareEmailTo('')
  }

  const handleShareEmailSend = async () => {
    const to = shareEmailTo.trim().split(/[\s,]+/).filter(Boolean)
    if (to.length === 0) {
      toast.error('Enter at least one email address.')
      return
    }
    setShareEmailSending(true)
    try {
      await meetingsAPI.shareReport(meetingId, { to: to.length === 1 ? to[0] : to })
      toast.success('Report sent', `Meeting report emailed to ${to.join(', ')}`)
      setShowEmailReportModal(false)
      setShareEmailTo('')
    } catch (err) {
      toast.error(err?.message ?? 'Failed to send email.')
    } finally {
      setShareEmailSending(false)
    }
  }

  // Close share menu on click outside
  useEffect(() => {
    const onMouseDown = (e) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) setShowShareMenu(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  // Mute participant
  const handleMuteParticipant = async (participant, shouldMute = true) => {
    const key = participant.id || participant.name
    setActionLoading((prev) => ({ ...prev, mute: key }))
    try {
      const response = shouldMute
        ? await cppBotAPI.mute({ name: participant.name, meetingId })
        : await cppBotAPI.unmute({ name: participant.name, meetingId })

      if (response?.success !== false) {
        toast.success(shouldMute ? 'Muted' : 'Unmuted', `${participant.name} has been ${shouldMute ? 'muted' : 'unmuted'}`)
        await fetchParticipants()
      } else {
        throw new Error(response.error?.message || 'Action failed')
      }
    } catch (error) {
      toast.error('Action Failed', error.message)
    } finally {
      setActionLoading((prev) => ({ ...prev, mute: null }))
    }
  }

  // Advance agenda
  const handleAdvanceAgenda = async () => {
    if (!window.confirm('Are you sure you want to advance to the next agenda item?')) return

    setActionLoading((prev) => ({ ...prev, advance: true }))
    try {
      const response = await agendaAPI.advance(meetingId)
      if (response?.success !== false) {
        toast.success('Agenda Advanced', 'Moved to the next agenda item')
        await fetchAgendaStatus()
      } else {
        throw new Error(response.error?.message || 'Failed to advance')
      }
    } catch (error) {
      toast.error('Advance Failed', error.message)
    } finally {
      setActionLoading((prev) => ({ ...prev, advance: false }))
    }
  }

  // End meeting
  const handleEndMeeting = async () => {
    const confirmLabel = isAssistantExternal ? 'leave this meeting' : 'end this meeting'
    const confirmed = await askEndMeetingConfirm(confirmLabel)
    if (!confirmed) return

    setActionLoading({ ...actionLoading, end: true })
    try {
      // Provider-aware bot cleanup. The server-side /cpp-bot/leave handler
      // short-circuits the C++ leave step for Teams meetings (Slice D) and
      // still runs Recall.stopBot + engines.cleanupMeeting — so the same
      // client call handles all three variants uniformly.
      //   - Zoom hosted: cppBotAPI.endAll → C++ Zoom SDK ends meeting for all.
      //   - Zoom assistant_external: cppBotAPI.leave → C++ leave + Recall stop.
      //   - Teams assistant_external: cppBotAPI.leave → server skips cpp,
      //     stops Recall, runs cleanup. No 404 user-facing error.
      if (isAssistantExternal) {
        await cppBotAPI.leave(meetingId)
      } else {
        await cppBotAPI.endAll(meetingId)
      }
      // Backend cleanupMeeting → markMeetingEndedInDb handles the DB status transition
      // for all variants. Optimistically update local state.
      setMeeting((prev) => ({ ...prev, status: 'ended' }))
      const successCopy = isExternalMeeting
        ? {
            title: 'Left Meeting',
            body: `Rithmo Transcription has stopped. You can close the ${isMeetMeeting ? 'Google Meet' : 'Teams meeting'} tab.`,
          }
        : isAssistantExternal
          ? { title: 'Left Meeting', body: 'Rithmo\'s bots have left the meeting. You can close the Zoom meeting tab.' }
          : { title: 'Meeting Ended', body: 'The bots have left the meeting.' }
      toast.success(successCopy.title, successCopy.body)
      setIsLive(false)
      await fetchMeetingData()
    } catch (error) {
      toast.error(isAssistantExternal || isExternalMeeting ? 'Leave Failed' : 'End Failed', error.message)
    } finally {
      setActionLoading({ ...actionLoading, end: false })
    }
  }

  // Join external meeting with Rithmo as a silent observer
  const joiningRef = useRef(false)
  const externalPollRef = useRef(null)
  // Cleanup external join polling on unmount or when meeting transitions to live/ended
  useEffect(() => {
    if (externalPollRef.current && meeting?.status && meeting.status !== 'starting') {
      clearInterval(externalPollRef.current)
      externalPollRef.current = null
    }
    return () => {
      if (externalPollRef.current) clearInterval(externalPollRef.current)
    }
  }, [meeting?.status])
  const handleJoinExternalWithRithmo = async () => {
    if (joiningRef.current) return // prevent double-click

    // Block recurring parent meetings — must use a specific occurrence
    if (meeting?.isRecurring && !meeting?.parentMeetingId) {
      toast.error('Recurring meetings are not yet supported for "Take Rithmo With You". Start a specific occurrence first.')
      return
    }

    const joinUrl = meeting?.joinUrl || meeting?.meetingUrl
    const password = meeting?.password
    if (!joinUrl) {
      toast.error('No join link configured.')
      return
    }
    setStartingMeeting(true)
    joiningRef.current = true
    try {
      await meetingsAPI.updateStatus(meetingId, { status: 'starting' })
      setMeeting((prev) => ({ ...prev, status: 'starting' }))

      const botRes = await botAPI.createZoomBot({
        meetingUrl: joinUrl,
        meetingPassword: password || undefined,
        meetingUUID: meetingId,
        meetingJoinKind: 'assistant_external',
        botDisplayName: user?.name ? `${user.name}'s Assistant` : undefined,
      })
      if (!botRes?.success && !botRes?.id) throw new Error(botRes?.message ?? 'Bot failed to join')

      // Don't mark 'live' immediately — bots are still connecting to Zoom.
      // The backend auto-start service transitions to 'live' once bots actually join.
      // We stay at 'starting' and poll until the backend confirms 'live'.
      window.open(joinUrl, '_blank')
      toast.success('Rithmo is joining', 'Bots are connecting to the meeting. Live insights will appear shortly.')
      // Poll meeting data + bot status until the backend auto-start marks the meeting 'live'.
      // Timeout after ~120s: if bots never join, revert to 'created' to prevent stuck state.
      let pollCount = 0
      if (externalPollRef.current) clearInterval(externalPollRef.current)
      externalPollRef.current = setInterval(async () => {
        pollCount++
        await fetchMeetingData()
        await fetchBotStatus()
        // Stop polling after the meeting transitions to live or after ~120s timeout.
        if (pollCount >= 40) {
          clearInterval(externalPollRef.current)
          externalPollRef.current = null
          // If still at 'starting' after timeout, revert to 'created'
          setMeeting((prev) => {
            if (prev?.status === 'starting') {
              toast.error('Bots did not connect in time. Please try again.')
              meetingsAPI.updateStatus(meetingId, { status: 'created' }).catch(() => {})
              return { ...prev, status: 'created' }
            }
            return prev
          })
        }
      }, 3000)
    } catch (err) {
      try { await meetingsAPI.updateStatus(meetingId, { status: 'created' }) } catch { /* ignore */ }
      setMeeting((prev) => ({ ...prev, status: 'created' }))
      toast.error(err?.message ?? 'Could not join meeting with Rithmo.')
    } finally {
      setStartingMeeting(false)
      joiningRef.current = false
    }
  }

  // Add Rithmo Meeting Manager after user has joined the Zoom meeting (Option 1 flow)
  const handleAddBotToMeeting = async () => {
    if (!addBotEligible || cppBotJoined || !addBotJoinUrl) return
    setAddingBot(true)
    try {
      const response = await zoomAPI.joinBotToMeeting({
        meetingId,
        joinUrl: addBotJoinUrl,
        password: addBotPassword ?? undefined,
      })
      if (response?.success === false) {
        throw new Error(response.error?.message || response.message || 'Failed to add bot')
      }
      setCppBotJoined(true)
      toast.success('Rithmo Meeting Manager Added', response?.message || 'The bot has joined your meeting.')
    } catch (error) {
      toast.error(
        'Could not add bot',
        error?.message || 'Make sure you are in the Zoom meeting and try again.'
      )
    } finally {
      setAddingBot(false)
    }
  }

  const scorecardModels = useMemo(() => ([
    { id: 'talk-equity', name: 'Talk Equity', description: 'Balanced participation across voices.' },
    { id: 'agenda-adherence', name: 'Agenda Adherence', description: 'Stayed aligned to planned topics.' },
    { id: 'interruptions', name: 'Conversation Flow', description: 'How smoothly the conversation flowed, with balanced turn-taking and minimal cross-talk.' },
    { id: 'decision-velocity', name: 'Output Velocity', description: 'Clear outcomes per meeting.' },
    { id: 'time-discipline', name: 'Time Discipline', description: 'Started and finished on time.' },
  ]), [])

  const analytics = useMemo(() => (
    meeting?.analytics && typeof meeting.analytics === 'object' ? meeting.analytics : null
  ), [meeting?.analytics])

  const agendaAdherenceBreakdown = useMemo(() => {
    const total = Number(analytics?.agendaItemCount ?? 0)
    const completed = Number(analytics?.agendaItemCompleted ?? 0)
    if (!total || total <= 0) return null
    return {
      total,
      completed,
      outstanding: Math.max(0, total - completed),
      tangentsDetected: Number(analytics?.tangentsDetectedCount ?? 0),
    }
  }, [analytics])

  const timeDisciplineDetail = useMemo(() => {
    const scheduledStart = meeting?.scheduledStartTime ? parseISO(meeting.scheduledStartTime) : null
    const scheduledEnd = meeting?.scheduledEndTime ? parseISO(meeting.scheduledEndTime) : null
    const actualStart = meeting?.startedAtMs != null ? new Date(meeting.startedAtMs) : null
    const actualEnd = meeting?.endedAtMs != null ? new Date(meeting.endedAtMs) : null
    const earlyEndMinutes = analytics?.earlyEndMinutes != null ? Number(analytics.earlyEndMinutes) : null
    const hasAny = scheduledStart != null || scheduledEnd != null || actualStart != null || actualEnd != null || earlyEndMinutes != null
    if (!hasAny) return null
    let timeLabel = null
    let diffMinutes = null
    if (scheduledStart && scheduledEnd && actualStart && actualEnd) {
      const scheduledDurationMin = (scheduledEnd - scheduledStart) / 60000
      const actualDurationMin = (actualEnd - actualStart) / 60000
      diffMinutes = Math.round((scheduledDurationMin - actualDurationMin) * 10) / 10
      timeLabel = diffMinutes > 0 ? `${diffMinutes} min under` : diffMinutes < 0 ? `${Math.abs(diffMinutes)} min over` : 'On time'
    } else if (earlyEndMinutes != null) {
      diffMinutes = earlyEndMinutes
      timeLabel = earlyEndMinutes > 0 ? `${earlyEndMinutes} min under` : earlyEndMinutes < 0 ? `${Math.abs(earlyEndMinutes)} min over` : 'On time'
    }
    return { scheduledStart, scheduledEnd, actualStart, actualEnd, timeUnderOver: timeLabel, diffMinutes }
  }, [meeting?.scheduledStartTime, meeting?.scheduledEndTime, meeting?.startedAtMs, meeting?.endedAtMs, analytics?.earlyEndMinutes])

  const participantBreakdowns = useMemo(() => {
    const stats = analytics?.participantStats && typeof analytics.participantStats === 'object' ? analytics.participantStats : null
    if (!stats) return {}
    const participantsList = Array.isArray(meeting?.participants) ? meeting.participants : []
    const nameById = new Map(participantsList.map((p) => [p.participantId ?? p.id, decodeHtmlEntities(p.displayName ?? p.name ?? p.email ?? '') || null]).filter(([id]) => id != null))
    const getName = (id) => nameById.get(id) ?? stats?.[id]?.displayName ? decodeHtmlEntities(String(stats[id].displayName)) : String(id)
    const isBot = (id) => /rithmo|recall|zoom bot|fireflies/i.test(String(getName(id)).toLowerCase()) || id === '200' || id === '300'
    const ids = Object.keys(stats).filter((id) => !isBot(id))
    if (ids.length === 0) return {}
    const rowsTalk = ids.map((id) => ({ name: getName(id), value: Math.round(Number(stats[id]?.talkTime ?? 0) * 10) / 10 })).sort((a, b) => b.value - a.value)
    const rowsInterruptions = ids.map((id) => ({ name: getName(id), value: Number(stats[id]?.interruptions ?? 0) })).sort((a, b) => b.value - a.value)
    return { 'talk-equity': { metricLabel: 'Talk time (minutes)', unit: 'min', rows: rowsTalk }, interruptions: { metricLabel: 'Interruptions caused (lower is better)', unit: 'count', rows: rowsInterruptions } }
  }, [analytics?.participantStats, meeting?.participants])

  const outputCounts = useMemo(() => {
    const outputs = Array.isArray(recapOutputs) ? recapOutputs : []
    const decisionCount = outputs.filter((o) => String(o.type ?? '').toLowerCase().replace(/-/g, '_') === 'decision').length
    const actionItemCount = outputs.filter((o) => String(o.type ?? '').toLowerCase().replace(/-/g, '_') === 'action_item').length
    const followUpCount = outputs.filter((o) => String(o.type ?? '').toLowerCase().replace(/-/g, '_') === 'follow_up').length
    return {
      decisionCount: outputs.length > 0 ? decisionCount : (analytics?.decisionCount != null ? Number(analytics.decisionCount) : 0),
      actionItemCount: outputs.length > 0 ? actionItemCount : (analytics?.actionItemCount != null ? Number(analytics.actionItemCount) : 0),
      followUpCount: outputs.length > 0 ? followUpCount : (analytics?.followUpCount != null ? Number(analytics.followUpCount) : 0),
    }
  }, [analytics?.decisionCount, analytics?.actionItemCount, analytics?.followUpCount, recapOutputs])

  const parentMeetingId = meeting?.parentMeetingId

  useEffect(() => {
    if (!meeting?.parentMeetingId) {
      setParentMeetingForPrep(null)
      return
    }
    let cancelled = false
    meetingsAPI
      .get(meeting.parentMeetingId)
      .then((res) => {
        if (cancelled) return
        const data = res?.data ?? res
        setParentMeetingForPrep(data?.meetingId ? data : null)
      })
      .catch(() => {
        if (!cancelled) setParentMeetingForPrep(null)
      })
    return () => {
      cancelled = true
    }
  }, [meeting?.parentMeetingId])

  const summaryPeople = useMemo(() => {
    if (!meeting) return []
    const hasIdentity = (p) => {
      const n = (p.displayName ?? p.name ?? p.userName ?? p.user_name ?? p.email ?? '').trim()
      return n.length > 0
    }
    const inviteSource =
      invitedParticipants.length > 0
        ? invitedParticipants
        : Array.isArray(meeting?.expectedParticipants) && meeting.expectedParticipants.length === 0
          ? []
          : meeting.participants || []
    const expected = inviteSource.filter((p) => !isParticipantBot(p) && hasIdentity(p))
    const rawActual = (
      isLive ? participants : (meeting.participants || []).filter((p) => p.joinedAtMs != null)
    ).filter((p) => !isParticipantBot(p) && hasIdentity(p))
    const actualEmailSet = new Set(
      rawActual.map((p) => (p.email ?? '').toLowerCase().trim()).filter(Boolean)
    )
    const actualFirstNameSet = new Set(
      rawActual
        .map((p) => {
          const n = (p.displayName ?? p.name ?? p.userName ?? p.user_name ?? '').trim().toLowerCase()
          return n.split(/\s+/)[0]
        })
        .filter(Boolean)
    )
    return expected.map((p, idx) => ({
      key: p.participantId ?? p.id ?? p.email ?? `person-${idx}`,
      name: participantDisplayName(p),
      email: (p.email ?? '').trim() || null,
      isHost: Boolean(p.isHost || p.role === 'host'),
      isLive: isLive && isInvitedParticipantCurrentlyLive(p, actualEmailSet, actualFirstNameSet),
    }))
  }, [invitedParticipants, isLive, meeting, participants])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="lg:ml-[280px] p-4 pt-20 lg:pt-8 lg:p-8">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          </div>
        </main>
      </div>
    )
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="lg:ml-[280px] p-4 pt-20 lg:pt-8 lg:p-8">
          <div className="text-center py-20">
            <AlertCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Meeting Not Found</h2>
            <p className="text-gray-500 mb-6">The meeting you're looking for doesn't exist.</p>
            <Link to="/dashboard" className="btn-primary">
              Back to Dashboard
            </Link>
          </div>
        </main>
      </div>
    )
  }

  const seriesPeriodParam = SERIES_SCORECARD_PERIOD_MAP[seriesAnalyticsTimeframe] || '30d'
  const { label: seriesPeriodLabelOnly, dateRangeText: seriesDateRangeText } = getPeriodLabelAndRange(
    seriesPeriodParam,
    seriesAnalytics?.label,
    seriesAnalytics?.periodStart,
    seriesAnalytics?.periodEnd
  )
  const seriesAnalyticsLabel = seriesDateRangeText
    ? `${seriesPeriodLabelOnly} (${seriesDateRangeText})`
    : (seriesAnalytics?.label ?? seriesPeriodLabelOnly)

  const pageTitle = decodeHtmlEntities(meeting.title ?? 'Meeting')
  const isObserveOnly = Boolean(meeting.isObserveOnly)
  // Recurring parents show the richer series scorecard (MeetingScorecardsSection) instead,
  // so exclude them here to avoid rendering two scorecards on the parent's Recap tab.
  const showScorecard = !isObserveOnly && !isParent && isCompleted && meeting.scorecard
  const totalScore = showScorecard ? overallScoreFromMeetingScorecard(meeting.scorecard) : null
  const actualDurationMinForScorecard =
    meeting.startedAtMs != null && meeting.endedAtMs != null
      ? (meeting.endedAtMs - meeting.startedAtMs) / 60000
      : null
  const shortMeetingNoScorecard =
    isCompleted &&
    !meeting.scorecard &&
    actualDurationMinForScorecard != null &&
    actualDurationMinForScorecard > 0 &&
    actualDurationMinForScorecard <= 5
  const agendaCompleteLive =
    isLive &&
    (agendaStatus?.items?.length ?? 0) > 0 &&
    (agendaStatus?.currentIndex ?? -1) < 0
  const showOutputs =
    isCompleted ||
    (isParent && (recapOutputs?.length ?? 0) > 0) ||
    (agendaCompleteLive && (recapOutputs?.length ?? 0) > 0) ||
    (isAssistantExternal && (recapOutputs?.length ?? 0) > 0)

  const toggleModel = (modelId) => {
    setExpandedModels((prev) => (
      prev.includes(modelId)
        ? prev.filter((id) => id !== modelId)
        : [...prev, modelId]
    ))
  }

  const tabs = meeting?.isImported
    ? [{ id: 'overview', label: 'Recap' }]
    : [
        { id: 'overview', label: 'Recap' },
        { id: 'controls', label: 'Live Meeting Insights' },
        ...(isAssistantExternal ? [{ id: 'chat', label: 'Chat with Rithmo' }] : []),
        { id: 'agenda', label: 'Prep' },
        ...(!isLive ? [{ id: 'followups', label: 'Follow-ups' }] : []),
        ...(isParent ? [{ id: 'history', label: 'Meeting History' }] : []),
      ]

  const renderBackButton = (compact = false) => (
    <button
      onClick={() => navigate(-1)}
      className={`${compact ? 'btn-secondary' : 'btn-ghost'} flex items-center gap-2`}
    >
      <ArrowLeft className="w-5 h-5" />
      Back
    </button>
  )

  const meetingScheduledStartIso = meeting.scheduledStartTime ?? meeting.startTime ?? null
  const meetingScheduledEndIso = meeting.scheduledEndTime ?? meeting.endTime ?? null
  const meetingScheduledDurationLabel = formatWallClockDurationLabel(
    scheduledWallClockDurationMinutes(meetingScheduledStartIso, meetingScheduledEndIso)
  )

  const showDeleteMeetingButton =
    !meeting?.isImported &&
    !isLive &&
    (meeting.isRecurring
      ? !['starting', 'live', 'paused', 'ending'].includes(String(meeting.status ?? ''))
      : meeting.status === 'created' && meeting.status !== 'starting')

  const summaryScheduleLine = (() => {
    const dateStr = meeting.startTime
      ? format(parseISO(meeting.startTime), 'EEEE, MMMM d, yyyy')
      : meeting.startedAtMs
        ? format(new Date(meeting.startedAtMs), 'EEEE, MMMM d, yyyy')
        : null
    const parts = [
      dateStr,
      meetingScheduledStartIso ? fmtTime(meetingScheduledStartIso) : null,
      meetingScheduledDurationLabel !== '—' ? meetingScheduledDurationLabel : null,
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(' · ') : 'Not set'
  })()

  const renderShareReportButton = () => (
    <div className="relative shrink-0" ref={shareMenuRef}>
      <button
        type="button"
        onClick={() => setShowShareMenu((v) => !v)}
        className="btn-secondary flex items-center gap-2"
      >
        <Share2 className="w-5 h-5" />
        Share Report
        <ChevronDown className={`w-4 h-4 transition-transform ${showShareMenu ? 'rotate-180' : ''}`} />
      </button>
      {showShareMenu && (
        <div className="absolute right-0 top-full mt-1 z-20 min-w-[180px] py-1 rounded-xl bg-white border border-gray-200 shadow-lg">
          <button
            type="button"
            onClick={handleShareEmailOpen}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <Mail className="w-4 h-4 text-gray-500" />
            Email report
          </button>
          <button
            type="button"
            onClick={handleShareCopy}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <Copy className="w-4 h-4 text-gray-500" />
            Copy report
          </button>
          <button
            type="button"
            onClick={handleShareDownload}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <Download className="w-4 h-4 text-gray-500" />
            Download report
          </button>
          {isCompleted && (
            <>
              <button
                type="button"
                onClick={() => handleTranscriptDownload('txt')}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4 text-gray-500" />
                Download transcript (.txt)
              </button>
              <button
                type="button"
                onClick={() => handleTranscriptDownload('pdf')}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4 text-gray-500" />
                Download transcript (.pdf)
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )

  const renderEndMeetingButton = (className = '') => (
    <button
      onClick={handleEndMeeting}
      disabled={actionLoading.end}
      className={`btn-secondary flex items-center gap-2 text-red-600 hover:bg-red-50 ${className}`.trim()}
    >
      {actionLoading.end ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <Square className="w-5 h-5" />
      )}
      {isAssistantExternal ? 'Leave Meeting' : 'End Meeting'}
    </button>
  )


  const meetingNarrativeCard = recapNarrativeSummary && isCompleted && !isParent ? (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="w-full"
    >
      <div className="overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-sm ring-1 ring-emerald-200/50 h-full">
        <div className="flex items-center gap-3 px-4 py-3 sm:px-5 sm:py-3.5">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-600">Meeting Summary</p>
        </div>
        <div className="border-t border-gray-100 px-4 pb-4 pt-3 sm:px-5 sm:pb-4">
          <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-line">{recapNarrativeSummary}</p>
        </div>
      </div>
    </motion.div>
  ) : null

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <MeetingSetupOverlay
        visible={startingMeeting}
        title="Setting Up Rithmo"
        subtitle="Starting your meeting..."
      />
      <MeetingSetupOverlay
        visible={!startingMeeting && addingBot}
        title="Connecting Rithmo"
        subtitle="Adding Rithmo Meeting Manager to your meeting..."
      />

      <main className={`lg:ml-[280px] p-4 pt-20 ${controlsTabActive ? 'lg:pt-6 lg:p-6' : 'lg:pt-8 lg:p-8'}`}>
        {/* Other live meeting banner — same pattern as dashboard */}
        {otherActiveMeeting && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-green-200 bg-green-50 px-5 py-3.5"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-green-900">
                  Meeting in Progress
                </p>
                <p className="text-xs text-green-700 truncate">
                  {decodeHtmlEntities(otherActiveMeeting.title) || 'Untitled meeting'}
                </p>
                <p className="text-xs text-green-600 mt-0.5">
                  You cannot start another meeting while one is already live.
                </p>
              </div>
            </div>
            <Link
              to={getMeetingLiveInsightsPath(otherActiveMeeting)}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 transition-colors"
            >
              <Radio className="w-4 h-4" />
              View Live Meeting Insights
            </Link>
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col gap-4 xl:grid xl:grid-cols-3 xl:items-start">
            <div className="flex justify-center xl:justify-start order-last xl:order-first">
              {showDeleteMeetingButton && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deletingMeeting}
                  className="shrink-0 px-6 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-60 flex items-center gap-2 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                  Delete Meeting
                </button>
              )}
            </div>
            <div className="min-w-0 text-center">
              <div className="flex flex-wrap items-center justify-center gap-3 mb-2">
                <ProviderLogo provider={meeting.provider} size={24} className="shrink-0" />
                <h1 className="text-3xl font-bold text-gray-900 break-words min-w-0">
                  {pageTitle}
                </h1>
                {!isParent && (
                  <span
                    className={`badge ${
                      isLive || meeting.status === 'starting'
                        ? 'badge-error'
                        : hasActiveChild
                        ? 'badge-error'
                        : isCompleted
                        ? 'badge-success'
                        : isScheduled
                        ? 'badge-accent'
                        : 'badge-warning'
                    }`}
                  >
                    {meeting.status === 'starting' ? (
                      <span className="flex items-center gap-1">
                        <Radio className="w-3 h-3 animate-pulse" />
                        Starting
                      </span>
                    ) : isLive ? (
                      <span className="flex items-center gap-1">
                        <Radio className="w-3 h-3 animate-pulse" />
                        Live
                      </span>
                    ) : hasActiveChild ? (
                      <span className="flex items-center gap-1">
                        <Radio className="w-3 h-3 animate-pulse" />
                        In progress
                      </span>
                    ) : (
                      meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1)
                    )}
                  </span>
                )}
                {isLive && meeting?.startedAtMs ? (
                  <MeetingElapsedTimer startedAtMs={meeting.startedAtMs} />
                ) : null}
                {/* Provider badge — surfaces the meeting platform so the user
                    can tell at a glance which one Rithmo is observing. */}
                {isTeamsMeeting ? (
                  <span className="badge bg-violet-50 text-violet-700 border-violet-200">
                    Microsoft Teams
                  </span>
                ) : isMeetMeeting ? (
                  <span className="badge bg-emerald-50 text-emerald-700 border-emerald-200">
                    Google Meet
                  </span>
                ) : isSlackMeeting ? (
                  <span className="badge bg-purple-50 text-purple-700 border-purple-200">
                    Slack Huddle
                  </span>
                ) : (
                  <span className="badge bg-sky-50 text-sky-700 border-sky-200">
                    Zoom
                  </span>
                )}
              </div>
              {summaryScheduleLine && summaryScheduleLine !== 'Not set' ? (
                <p className="text-sm text-gray-500 tabular-nums">{summaryScheduleLine}</p>
              ) : null}
              {decodeHtmlEntities(meeting.description) ? (
                <p className="max-w-4xl text-gray-500 mx-auto mt-1">
                  {decodeHtmlEntities(meeting.description)}
                </p>
              ) : null}
              {summaryPeople.length > 0 ? (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                  {summaryPeople.map((person) => (
                    <span
                      key={person.key}
                      className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/70 py-1 pl-1 pr-3 text-sm"
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-primary text-xs font-semibold text-white">
                        {(person.name ?? 'P').charAt(0).toUpperCase()}
                      </span>
                      <span className="font-medium text-gray-900">{person.name}</span>
                      {person.isHost ? (
                        <span className="text-xs font-medium text-primary-700">Host</span>
                      ) : null}
                      {person.isLive ? (
                        <span className="text-xs font-medium text-green-700">Live</span>
                      ) : null}
                    </span>
                  ))}
                </div>
              ) : null}
              {controlsTabActive && isChild && parentMeetingId && (
                <div className="mt-2">
                  <Link
                    to={`/meeting/${parentMeetingId}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                  >
                    <Repeat className="w-3.5 h-3.5" />
                    Back to series
                  </Link>
                </div>
              )}
              {isAssistantExternal && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200">
                  <Bot className="w-4 h-4 text-indigo-600 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-indigo-800">Rithmo Assistant Mode</p>
                    <p className="text-xs text-indigo-600">Rithmo joins silently — live insights for you only</p>
                  </div>
                </div>
              )}
              {meeting?.isImported && (
                <div className="mt-3 space-y-3">
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 border border-blue-200">
                    <Calendar className="w-4 h-4 text-blue-600 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-blue-800">
                        Imported from {meeting.calendarProvider === 'google' ? 'Google Calendar' : meeting.calendarProvider === 'microsoft' ? 'Outlook' : 'External Calendar'}
                      </p>
                      <p className="text-xs text-blue-600">This meeting was imported from your calendar</p>
                    </div>
                  </div>
                  {meeting.joinUrl && meeting.joinUrl.includes('zoom.us') ? (
                    <div className="flex flex-col gap-2">
                      <a
                        href={meeting.joinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition w-fit"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Join Zoom Meeting
                      </a>
                      <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 w-fit">
                        <Bot className="w-4 h-4 text-purple-600 shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-purple-800">Send Rithmo as Proxy</p>
                          <p className="text-xs text-purple-600">Coming soon — Rithmo will join this Zoom on your behalf</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {meeting.joinUrl && (
                        <a
                          href={meeting.joinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition w-fit"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {isMeetMeeting || meeting.joinUrl.includes('meet.google')
                            ? 'Join Google Meet'
                            : isTeamsMeeting || meeting.joinUrl.includes('teams.microsoft')
                            ? 'Join Microsoft Teams'
                            : 'Join Meeting'}
                        </a>
                      )}
                      <div className="rounded-xl bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 p-4">
                        <div className="flex items-start gap-3">
                          <Bot className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-gray-700">Rithmo integration coming soon</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Rithmo currently supports Zoom meetings. Support for {isMeetMeeting || meeting.joinUrl?.includes('meet.google') ? 'Google Meet' : isTeamsMeeting || meeting.joinUrl?.includes('teams.microsoft') ? 'Microsoft Teams' : 'this platform'} is on the way — you'll be able to send Rithmo as a proxy to manage your meetings across platforms.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              className={`flex flex-wrap items-center gap-3 ${!controlsTabActive ? 'xl:justify-end' : ''} ${showStartBlockedSubtextRow ? 'pb-8' : ''}`}
            >
              {controlsTabActive ? renderBackButton(true) : null}
              {(meeting.joinUrl || meeting.meetingUrl) && !meeting?.isImported && !isCompleted && (
                <a
                  href={meeting.joinUrl || meeting.meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition"
                >
                  <ExternalLink className="w-4 h-4" />
                  {isTeamsMeeting ? 'Join Teams' : isMeetMeeting ? 'Join Google Meet' : 'Join Zoom'}
                </a>
              )}
              {(canEditAgenda && !isLive) && (!isAssistantExternal || isExternalJoinEnabled(user?.email)) && (
                <Link
                  to={isAssistantExternal ? `/join-external?edit=${meeting.id}` : `/agenda/${meeting.id}?edit=1`}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Edit2 className="w-5 h-5" />
                  Edit
                </Link>
              )}
              {isScheduled && !meeting?.isImported && (
                <Link
                  to={`/agenda/${meeting.id}`}
                  className="btn-primary flex items-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  Start Meeting
                </Link>
              )}
              {canStartMeeting && !isAssistantExternal && (
                <button
                  onClick={handleStartMeeting}
                  disabled={startingMeeting}
                  className="btn-primary flex items-center gap-2 disabled:opacity-70"
                >
                  {startingMeeting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5" />
                      {isStartNextOccurrence ? 'Start next occurrence' : 'Start Meeting'}
                    </>
                  )}
                </button>
              )}
              {canStartMeeting && isAssistantExternal && isExternalJoinEnabled(user?.email) && (
                <button
                  onClick={handleJoinExternalWithRithmo}
                  disabled={startingMeeting}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-70 transition"
                  style={{ background: startingMeeting ? '#a5b4fc' : 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
                >
                  {startingMeeting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <Bot className="w-5 h-5" />
                      Join Meeting with Rithmo
                    </>
                  )}
                </button>
              )}
              {/* Blocked: Zoom not connected */}
              {!canStartMeeting && !meeting?.isImported && isZoomMeeting && !zoomConnected && !hasActiveChild && !otherActiveMeeting &&
                (meeting?.status === 'created' || (meeting?.isRecurring && meeting?.status === 'ended')) && (
                <div className="relative shrink-0">
                  <button
                    disabled
                    className="btn-primary flex items-center gap-2 opacity-50 cursor-not-allowed"
                  >
                    <Play className="w-5 h-5" />
                    {isStartNextOccurrence ? 'Start next occurrence' : 'Start Meeting'}
                  </button>
                  <div className="pointer-events-auto absolute top-full right-0 mt-1.5 flex items-center justify-end gap-1.5 text-xs text-amber-700 max-w-xs text-right">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span>Zoom is not connected. <a href={connectZoomUrl || '#'} className="font-semibold underline hover:text-amber-900">Connect Zoom</a> to start this meeting.</span>
                  </div>
                </div>
              )}
              {/* Blocked: another meeting is already live */}
              {!canStartMeeting && !meeting?.isImported && otherActiveMeeting &&
                (meeting?.status === 'created' || (meeting?.isRecurring && meeting?.status === 'ended')) && (
                <div className="relative shrink-0">
                  <button
                    disabled
                    className="btn-primary flex items-center gap-2 opacity-50 cursor-not-allowed"
                  >
                    <Play className="w-5 h-5" />
                    {isStartNextOccurrence ? 'Start next occurrence' : 'Start Meeting'}
                  </button>
                  <div className="pointer-events-auto absolute top-full right-0 mt-1.5 flex items-center justify-end gap-1.5 text-xs text-amber-700 max-w-xs text-right">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span>
                      <Link
                        to={getMeetingLiveInsightsPath(otherActiveMeeting)}
                        className="font-semibold underline hover:text-amber-900"
                      >
                        {decodeHtmlEntities(otherActiveMeeting.title) || 'Another meeting'}
                      </Link>
                      {' '}is already in progress.
                    </span>
                  </div>
                </div>
              )}
              {isLive && !controlsTabActive && isMeetingOrganizer ? renderEndMeetingButton() : null}
            </div>
          </div>
        </motion.div>

        {/* Back button — left-aligned, above the tabs */}
        {!controlsTabActive ? (
          <div className="mb-3 flex items-center gap-3">
            {isChild && parentMeetingId ? (
              <Link
                to={`/meeting/${parentMeetingId}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
              >
                <Repeat className="w-3.5 h-3.5" />
                Back to series
              </Link>
            ) : (
              renderBackButton()
            )}
          </div>
        ) : null}

        {/* Tabs */}
        <div className={`border-b border-gray-200 ${controlsTabActive ? 'mb-4 pb-2' : 'mb-6 pb-4'}`}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-primary text-white'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {((isCompleted || isParent) || (controlsTabActive && isLive && isMeetingOrganizer)) ? (
              <div className="ml-auto flex flex-wrap items-center gap-3 shrink-0">
                {(isCompleted || isParent) ? renderShareReportButton() : null}
                {controlsTabActive && isLive && isMeetingOrganizer ? renderEndMeetingButton('shrink-0') : null}
              </div>
            ) : null}
          </div>
        </div>

        {/* Ask-this-meeting search bar — below the tab bar, above the summary.
            Only for meetings that have happened (own transcript). Hidden on the
            Follow-ups tab, which is dedicated solely to drafting the follow-up email. */}
        {isCompleted && !isParent && meetingId && activeTab !== 'followups' && (
          <MeetingSearchBar meetingId={meetingId} />
        )}

        {/* End meeting confirmation modal */}
        {endMeetingConfirm?.open && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => resolveEndMeetingConfirm(false)}
          >
            <div
              className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isAssistantExternal ? 'Leave meeting?' : 'End meeting?'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Are you sure you want to {endMeetingConfirm.label}?
                    {!isAssistantExternal && ' This will end the meeting for all participants.'}
                  </p>
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-5">
                <button
                  type="button"
                  onClick={() => resolveEndMeetingConfirm(false)}
                  className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => resolveEndMeetingConfirm(true)}
                  className="px-4 py-2 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700"
                  autoFocus
                >
                  {isAssistantExternal ? 'Leave' : 'End meeting'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Concurrent Zoom call confirmation modal */}
        {concurrentConfirm?.open && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => resolveConcurrentConfirm(false)}
          >
            <div
              className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900">
                    You're already in a Zoom call
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Starting this meeting will end your current Zoom call. Are you sure you want
                    to continue?
                  </p>
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-5">
                <button
                  type="button"
                  onClick={() => resolveConcurrentConfirm(false)}
                  className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => resolveConcurrentConfirm(true)}
                  className="btn-primary"
                  autoFocus
                >
                  End other call & continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !deletingMeeting && setShowDeleteConfirm(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {meeting?.isRecurring ? 'Remove recurring series?' : 'Delete meeting?'}
              </h3>
              <p className="text-gray-600 mb-6">
                {meeting?.isRecurring
                  ? 'This will delete the recurring meeting and send a cancellation to participants. Are you sure you want to delete the recurring meeting?'
                  : 'Are you sure you want to delete this meeting?'}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deletingMeeting}
                  className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setDeletingMeeting(true)
                    try {
                      await meetingsAPI.delete(meetingId)
                      markInvitationSeen(meetingId)
                      markPastDueSeen(meetingId)
                      setShowDeleteConfirm(false)
                      if (meeting?.isRecurring) {
                        toast.success('Series removed', 'The recurring meeting has been deleted.')
                      } else {
                        toast.success('Meeting deleted', 'The meeting has been removed.')
                      }
                      navigate('/dashboard')
                    } catch (err) {
                      const msg = err?.response?.data?.error?.message ?? err?.message ?? 'Could not delete meeting'
                      toast.error('Delete failed', msg)
                    } finally {
                      setDeletingMeeting(false)
                    }
                  }}
                  disabled={deletingMeeting}
                  className="px-4 py-2 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 flex items-center gap-2"
                >
                  {deletingMeeting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email report modal */}
        {showEmailReportModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !shareEmailSending && (setShowEmailReportModal(false), setShareEmailTo(''))}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Email report</h3>
              <p className="text-gray-600 mb-4">Send the meeting report to one or more email addresses (comma-separated).</p>
              <input
                type="text"
                inputMode="email"
                placeholder="email@example.com or comma-separated"
                value={shareEmailTo}
                onChange={(e) => setShareEmailTo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleShareEmailSend()}
                className="input-field w-full mb-6"
                autoFocus
              />
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => { setShowEmailReportModal(false); setShareEmailTo(''); }}
                  disabled={shareEmailSending}
                  className="px-4 py-2 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleShareEmailSend}
                  disabled={shareEmailSending || !shareEmailTo.trim()}
                  className="btn-primary flex items-center gap-2 disabled:opacity-60"
                >
                  {shareEmailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Send
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Meeting in progress - when a child is live, block starting another occurrence */}
        {activeTab === 'overview' && hasActiveChild && !isLive && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-2xl bg-amber-50 border border-amber-200"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                <Radio className="w-6 h-6 text-white animate-pulse" />
              </div>
              <div>
                <p className="font-semibold text-amber-900">Meeting in progress</p>
                <p className="text-sm text-amber-700">
                  A meeting is already running. Please wait for it to end before starting another occurrence.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Failure banner — external Recall meeting bot couldn't join (host
            denied admission, meeting URL invalid/expired, or Recall-side
            fatal). Same UX for Teams and Meet; copy adapts to provider. */}
        {activeTab === 'overview' && isExternalMeeting && (isBotFailedToJoin || isBotFatal) && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 rounded-2xl bg-rose-50 border border-rose-200 flex items-start gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center shrink-0">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-rose-900">
                Rithmo couldn't join your {isMeetMeeting ? 'Google Meet' : 'Teams meeting'}
              </p>
              <p className="text-sm text-rose-800 mt-0.5 leading-relaxed">
                {isBotFailedToJoin
                  ? `The ${isMeetMeeting ? 'Meet call' : 'Teams meeting'} may have ended, the host may have denied the bot from the lobby, or the meeting link may have expired. Double-check the ${isMeetMeeting ? 'Meet' : 'Teams'} link and try again.`
                  : `Something went wrong on Rithmo's side. Please retry by clicking Start Meeting, or paste a fresh ${isMeetMeeting ? 'Meet' : 'Teams'} link.`}
              </p>
            </div>
          </motion.div>
        )}

        {/* Lobby admission banner — external Recall meetings (Teams + Meet).
            Visible while the Recall bot is sitting in the lobby waiting for
            the host to admit it. Clears automatically when the bot moves to
            in_call_recording (the meeting becomes 'live'). Wording branches
            per provider — Teams uses "Admit", Meet uses "Approve" in its
            "Ask to join" UI. */}
        {activeTab === 'overview' && isExternalMeeting && isBotInLobby && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center shrink-0">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-amber-900">
                {isMeetMeeting
                  ? 'Approve Rithmo Transcription from your Meet lobby'
                  : 'Admit Rithmo Transcription from your Teams lobby'}
              </p>
              <p className="text-sm text-amber-800 mt-0.5 leading-relaxed">
                {isMeetMeeting ? (
                  <>The Recall bot is waiting in your Meet meeting's lobby. In Google Meet, look for the &ldquo;Ask to join&rdquo; notification and click <strong>Approve</strong> next to &ldquo;Rithmo Transcription&rdquo;. Transcription begins as soon as the bot enters the call.</>
                ) : (
                  <>The Recall bot is waiting in your Teams meeting's lobby. In Teams, click <strong>Admit</strong> next to &ldquo;Rithmo Transcription&rdquo;. Transcription begins as soon as the bot enters the call.</>
                )}
              </p>
            </div>
          </motion.div>
        )}

        {/* External Recall-driven live status indicator — visible once the
            bot is in-call and transcribing on Teams or Meet. Subtle, just
            confirms the pipeline is healthy. */}
        {activeTab === 'overview' && isExternalMeeting && isBotTranscribingLive && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-3 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center gap-3"
          >
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
            <p className="text-sm text-emerald-900">
              Rithmo Transcription is in your {isMeetMeeting ? 'Google Meet' : 'Teams meeting'} — capturing decisions, action items, and live insights.
            </p>
          </motion.div>
        )}

        {/* Add Rithmo Meeting Manager - only when auto-join is known to have failed */}
        {activeTab === 'overview' && addBotEligible && botAutoJoinFailed && !cppBotJoined && addBotJoinUrl && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 rounded-2xl bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-indigo-900">Add Rithmo Meeting Manager to this meeting</p>
                <p className="text-sm text-indigo-700">
                  {isLive
                    ? 'Click to bring Rithmo into the call.'
                    : 'Start the meeting above, then click here to add Rithmo to the call.'}
                </p>
              </div>
            </div>
            <button
              onClick={handleAddBotToMeeting}
              disabled={addingBot}
              className="btn-secondary shrink-0 flex items-center gap-2 disabled:opacity-60"
            >
              {addingBot ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding bot...
                </>
              ) : (
                <>
                  <Bot className="w-4 h-4" />
                  {"I'm in the meeting – Add Rithmo Meeting Manager"}
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Live Controls Tab */}
          {activeTab === 'controls' &&
            (isLive ? (
            <div className={`space-y-4 ${compactLiveControlsMode ? 'lg:flex lg:h-[calc(100dvh-15rem)] lg:flex-col lg:overflow-hidden' : ''}`}>
              <div className={compactLiveControlsMode ? 'lg:min-h-0 lg:flex-1' : ''}>
                <MeetingLiveInsightsPanel
                  insights={liveInsights}
                  loading={liveInsightsLoading}
                  error={liveInsightsError}
                  readOnly={false}
                  onPersonalAction={postPersonalLiveInsightAction}
                  meetingStartedAtMs={meeting?.startedAtMs ?? null}
                  recapOutputs={recapOutputs}
                  memberPrivateGoalsState={memberPrivateGoalsState}
                  meetingId={meetingId}
                  audienceMode={meeting?.audienceMode ?? 'internal'}
                  lateStart={ioEnvelope?.lateStart ?? null}
                  onResolveLateStart={postLateStartResolution}
                  onUndoLateStart={postLateStartUndo}
                  onRefreshLateStart={refreshInternalOperator}
                  className={compactLiveControlsMode ? 'lg:h-full lg:min-h-0' : ''}
                />
              </div>

            </div>
            ) : (
            <div className="space-y-6">
              <div
                className={`rounded-2xl p-5 ${
                  isLiveInsightsProxyingActiveOccurrence
                    ? 'border border-blue-200 bg-blue-50'
                    : 'border border-amber-200 bg-amber-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                      isLiveInsightsProxyingActiveOccurrence ? 'bg-blue-100' : 'bg-amber-100'
                    }`}
                  >
                    <AlertCircle
                      className={`w-5 h-5 ${
                        isLiveInsightsProxyingActiveOccurrence ? 'text-blue-700' : 'text-amber-700'
                      }`}
                    />
                  </div>
                  <div>
                    <p className={`font-semibold ${isLiveInsightsProxyingActiveOccurrence ? 'text-blue-900' : 'text-amber-900'}`}>
                      {isLiveInsightsProxyingActiveOccurrence ? 'Viewing Active Occurrence' : 'Live Meeting Insights disabled'}
                    </p>
                    <p className={`text-sm mt-1 ${isLiveInsightsProxyingActiveOccurrence ? 'text-blue-800' : 'text-amber-800'}`}>
                      {nonLiveControlsBannerText}
                    </p>
                  </div>
                </div>
              </div>

              {canRequestLiveInsights ? (
                <MeetingLiveInsightsPanel
                  insights={liveInsights}
                  loading={liveInsightsLoading}
                  error={liveInsightsError}
                  readOnly
                  recapOutputs={recapOutputs}
                  meetingId={meetingId}
                  audienceMode={meeting?.audienceMode ?? 'internal'}
                  emptyMessage={
                    isParent
                      ? 'The most recent completed occurrence will appear here after a live session ends.'
                      : 'The final saved Live Meeting Insights snapshot will appear here after the meeting ends.'
                  }
                />
              ) : null}

              {/* Disabled controls showcase removed — post-meeting view shows only the read-only snapshot above */}
            </div>
            ))}

          {activeTab === 'chat' && isAssistantExternal && (
            <ChatWithRithmoPanel
              meetingId={meetingId}
              isLive={isLive}
              wsRef={wsRef}
            />
          )}

          {activeTab === 'overview' && (
            <div className="space-y-6">
            <RecapPersonalSection coaching={coaching} summarySlot={meetingNarrativeCard} />
            {isParent && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1">
                      {[
                        ['week', 'Week'],
                        ['month', 'Month'],
                        ['year', 'Year'],
                        ['all', 'All time'],
                      ].map(([id, label]) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setSeriesAnalyticsTimeframePersist(id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            seriesAnalyticsTimeframe === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 sm:text-right">Showing {seriesAnalyticsLabel}</p>
                </div>
                <MeetingScorecardsSection
                  periodDashboard={seriesAnalytics}
                  loading={seriesAnalyticsLoading}
                  analyticsLabel={seriesAnalyticsLabel}
                  detailPeriodLabel={SERIES_SCORECARD_DETAIL_PERIOD_LABELS[seriesAnalyticsTimeframe] ?? 'Period avg'}
                  chartRange={seriesAnalyticsTimeframe}
                  subtitle="Scores use the same models as Analytics, aggregated across completed occurrences in this recurring series."
                  scoreTrendGranularity="per_meeting"
                  onScoreTrendMeetingNavigate={(targetMeetingId) => {
                    navigate(`/meeting/${encodeURIComponent(targetMeetingId)}`)
                  }}
                  trendChartDescription="Click a point to open that occurrence. One point per qualifying meeting (chronological)."
                />
              </div>
            )}
            <div className="grid lg:grid-cols-2 gap-6 items-start">
              {shortMeetingNoScorecard && (
                <div className="glass rounded-2xl p-6 lg:col-span-2 border border-gray-100 bg-white/60">
                  <p className="text-sm text-gray-600">
                    Scorecard not generated for very short meetings (about five minutes or less).
                  </p>
                </div>
              )}
              {/* Observe-only baseline placeholder — shown instead of scorecard for first 3 meetings */}
              {isObserveOnly && isCompleted && (
                <div className="glass rounded-2xl p-6 lg:col-span-2 border border-[#16c1b2]/20 bg-[#e4faf8]/40">
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#e4faf8] text-[#0d8f84] mt-0.5">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Baseline still being built</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Rithmo is in observation mode for your first three meetings. Scorecards will appear once your baseline is set.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Meeting scorecard - for completed meetings or parent with consolidated scorecard */}
              {showScorecard && (
                <div className="glass rounded-2xl p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <ClipboardCheck className="w-5 h-5 text-primary-600" />
                      Meeting Scorecard
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Policy model scores for this meeting (0–100). Click a model to see details.
                    </p>
                  </div>
                  <div className="mb-6 rounded-2xl border border-gray-100 bg-white/70 p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Total Score</p>
                        <p className="text-4xl font-bold text-gray-900">
                          {totalScore != null ? totalScore : '—'}
                        </p>
                      </div>
                      <div className="w-full md:w-56">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${totalScore != null ? totalScore : 0}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-2 text-right">/100</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {scorecardModels.map((model, index) => {
                      const raw = meeting.scorecard[model.id]
                      const score =
                        typeof raw === 'number' && Number.isFinite(raw) ? raw : null
                      const isExpanded = expandedModels.includes(model.id)
                      const participantDetail = participantBreakdowns[model.id]
                      return (
                        <motion.div
                          key={model.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.05 * index }}
                          className="rounded-2xl border border-gray-100 bg-white/70 p-4"
                        >
                          <button
                            type="button"
                            onClick={() => toggleModel(model.id)}
                            className="w-full text-left"
                            aria-expanded={isExpanded}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <p className="font-medium text-gray-900">{model.name}</p>
                                <p className="text-sm text-gray-500">{model.description}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <p className="text-lg font-semibold text-gray-900">
                                    {score != null ? score : 'Not available'}
                                  </p>
                                  {score != null ? (
                                    <p className="text-xs text-gray-400">/100</p>
                                  ) : null}
                                </div>
                                <ChevronDown
                                  className={`w-4 h-4 text-gray-400 transition-transform ${
                                    isExpanded ? 'rotate-180' : ''
                                  }`}
                                />
                              </div>
                            </div>
                          </button>
                          <div className="progress-bar mt-3">
                            <div
                              className="progress-fill"
                              style={{ width: `${score != null ? score : 0}%` }}
                            />
                          </div>

                          {isExpanded && (
                            <div className="mt-4 border-t border-gray-200 pt-4">
                              {model.id === 'talk-equity' && participantDetail?.rows?.length ? (
                                <>
                                  <div className="flex items-center justify-between gap-2 mb-3">
                                    <div>
                                      <p className="text-sm font-semibold text-gray-900">Participant breakdown</p>
                                      <p className="text-xs text-gray-400">{participantDetail.metricLabel}</p>
                                    </div>
                                    <span className="text-xs text-gray-400">This meeting</span>
                                  </div>
                                  <div className="space-y-3">
                                    {participantDetail.rows.map((row) => (
                                      <div key={row.name} className="flex items-center justify-between gap-4">
                                        <p className="font-medium text-gray-900">{row.name}</p>
                                        <div className="text-right">
                                          <p className="text-sm font-medium text-gray-900">
                                            {participantDetail.unit === 'min' ? `${row.value}m` : row.value}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              ) : model.id === 'interruptions' && participantDetail?.rows?.length ? (
                                <>
                                  <div className="flex items-center justify-between gap-2 mb-3">
                                    <div>
                                      <p className="text-sm font-semibold text-gray-900">Participant breakdown</p>
                                      <p className="text-xs text-gray-400">{participantDetail.metricLabel}</p>
                                    </div>
                                    <span className="text-xs text-gray-400">This meeting</span>
                                  </div>
                                  <div className="space-y-3">
                                    {participantDetail.rows.map((row) => (
                                      <div key={row.name} className="flex items-center justify-between gap-4">
                                        <p className="font-medium text-gray-900">{row.name}</p>
                                        <div className="text-right">
                                          <p className="text-sm font-medium text-gray-900">{row.value}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {analytics?.interruptionCount != null && (
                                    <div className="flex items-baseline gap-x-2 mt-3 pt-3 border-t border-gray-100">
                                      <span className="text-gray-500 text-sm shrink-0">Total interruptions:</span>
                                      <span className="text-gray-900 font-medium text-sm">
                                        {Number(analytics.interruptionCount)}
                                      </span>
                                    </div>
                                  )}
                                </>
                              ) : model.id === 'agenda-adherence' && agendaAdherenceBreakdown ? (
                                <>
                                  <p className="text-sm font-semibold text-gray-900 mb-3">Agenda items</p>
                                  <div className="flex flex-wrap gap-4">
                                    <div>
                                      <p className="text-2xl font-semibold text-gray-900">{agendaAdherenceBreakdown.total}</p>
                                      <p className="text-xs text-gray-400">total</p>
                                    </div>
                                    <div>
                                      <p className="text-2xl font-semibold text-gray-900">{agendaAdherenceBreakdown.completed}</p>
                                      <p className="text-xs text-gray-400">completed</p>
                                    </div>
                                    <div>
                                      <p className="text-2xl font-semibold text-gray-900">{agendaAdherenceBreakdown.outstanding}</p>
                                      <p className="text-xs text-gray-400">outstanding / never started</p>
                                    </div>
                                    <div>
                                      <p className="text-2xl font-semibold text-gray-900">{agendaAdherenceBreakdown.tangentsDetected}</p>
                                      <p className="text-xs text-gray-400">tangents detected</p>
                                    </div>
                                  </div>
                                </>
                              ) : model.id === 'decision-velocity' ? (
                                <>
                                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                                    <p className="text-sm font-semibold text-gray-900">Output counts</p>
                                  </div>
                                  <div className="flex flex-wrap gap-4">
                                    <div>
                                      <p className="text-2xl font-semibold text-gray-900">{outputCounts.decisionCount}</p>
                                      <p className="text-xs text-gray-400">decisions</p>
                                    </div>
                                    <div>
                                      <p className="text-2xl font-semibold text-gray-900">{outputCounts.actionItemCount}</p>
                                      <p className="text-xs text-gray-400">action items</p>
                                    </div>
                                    <div>
                                      <p className="text-2xl font-semibold text-gray-900">{outputCounts.followUpCount}</p>
                                      <p className="text-xs text-gray-400">follow ups</p>
                                    </div>
                                  </div>
                                </>
                              ) : model.id === 'time-discipline' && timeDisciplineDetail ? (
                                <>
                                  <p className="text-sm font-semibold text-gray-900 mb-3">Scheduled vs actual</p>
                                  <table className="w-full text-sm text-left border-none" style={{ borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr>
                                        <th className="text-gray-500 font-medium pb-2 pr-6 w-0" scope="col" />
                                        <th className="text-gray-500 font-medium pb-2 pr-6" scope="col">Scheduled</th>
                                        <th className="text-gray-500 font-medium pb-2 pr-6" scope="col">Actual</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td className="text-gray-500 py-1.5 pr-6 align-top">Start</td>
                                        <td className="text-gray-700 py-1.5 pr-6 align-top">
                                          {timeDisciplineDetail.scheduledStart
                                            ? format(timeDisciplineDetail.scheduledStart, 'MMM d, yyyy h:mm a')
                                            : '—'}
                                        </td>
                                        <td className="text-gray-900 font-medium py-1.5 pr-6 align-top">
                                          {timeDisciplineDetail.actualStart
                                            ? format(timeDisciplineDetail.actualStart, 'MMM d, yyyy h:mm a')
                                            : '—'}
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="text-gray-500 py-1.5 pr-6 align-top">End</td>
                                        <td className="text-gray-700 py-1.5 pr-6 align-top">
                                          {timeDisciplineDetail.scheduledEnd
                                            ? format(timeDisciplineDetail.scheduledEnd, 'MMM d, yyyy h:mm a')
                                            : '—'}
                                        </td>
                                        <td className="text-gray-900 font-medium py-1.5 pr-6 align-top">
                                          {timeDisciplineDetail.actualEnd
                                            ? format(timeDisciplineDetail.actualEnd, 'MMM d, yyyy h:mm a')
                                            : '—'}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                  {timeDisciplineDetail.timeUnderOver != null && (
                                    <div className="flex items-baseline gap-x-2 mt-3 pt-3 border-t border-gray-100">
                                      <span className="text-gray-500 text-sm shrink-0">Total time:</span>
                                      <span
                                        className={
                                          (timeDisciplineDetail.diffMinutes ?? 0) < 0
                                            ? 'text-amber-600 font-medium text-sm'
                                            : (timeDisciplineDetail.diffMinutes ?? 0) > 0
                                              ? 'text-emerald-600 font-medium text-sm'
                                              : 'text-gray-900 font-medium text-sm'
                                        }
                                      >
                                        {timeDisciplineDetail.timeUnderOver}
                                      </span>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm text-gray-400">No detail available for this metric.</p>
                              )}
                            </div>
                          )}
                        </motion.div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Meeting outputs — ended meetings, recurring parent, or live after agenda complete */}
              <div className={`glass rounded-2xl p-6${!showScorecard ? ' lg:col-span-2' : ''}`}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <ListTodo className="w-5 h-5 text-primary-600" />
                  Meeting Outputs
                </h3>
                {agendaCompleteLive && (recapOutputs?.length ?? 0) > 0 && (
                  <p className="text-sm text-primary-700 bg-primary-50/80 border border-primary-100 rounded-lg px-3 py-2 mb-4">
                    Agenda complete — you can edit outputs below; changes are saved and will carry through when the meeting ends.
                  </p>
                )}
                {!showOutputs ? (
                  <p className="text-gray-500 py-4">
                    {isLive
                      ? 'Decisions and action items will appear here when captured, and after the last agenda item you can edit them here before the meeting ends.'
                      : 'Decisions and action items will appear here after the meeting has ended.'}
                  </p>
                ) : (() => {
                  const outputs = recapOutputs
                  const finished = outputs.filter((o) => closedOutputIds.has(o.id))
                  const outstanding = outputs.filter((o) => !closedOutputIds.has(o.id))
                  const showFinishedSection = !isParent
                  const toggleOutput = async (o, finishedState) => {
                    const sid = o.sourceMeetingId || meetingId
                    setOutputStatusLoading((prev) => ({ ...prev, [o.id]: true }))
                    try {
                      await meetingsAPI.updateOutputStatus(sid, o.id, { finished: finishedState, sourceMeetingId: sid })
                      setClosedOutputIds((prev) => {
                        const next = new Map(prev)
                        if (finishedState) next.set(o.id, Date.now())
                        else next.delete(o.id)
                        return next
                      })
                    } catch (err) {
                      const msg = err instanceof Error ? err.message : String(err ?? '')
                      toast.error('Could not update output', msg || 'Only the organizer can update outputs, or this output is locked.')
                    } finally {
                      setOutputStatusLoading((prev) => ({ ...prev, [o.id]: false }))
                    }
                  }
                  // Decisions are split by the strategy gate: real strategy is elevated under
                  // "Strategy"; tactical (operational) and garbage (noise) decisions are demoted
                  // to "Other decisions" rather than hidden, since the recap is the meeting
                  // record (strict hiding happens at the ledger). Un-annotated decisions (older
                  // recaps, or the gate disabled) fail open into Strategy.
                  const categoryLabel = (o) => {
                    const t = (o?.type || '').toLowerCase().replace(/-/g, '_')
                    if (t === 'decision') {
                      const v = o?.strategy?.verdict
                      return v === 'operational' || v === 'noise' ? 'Other decisions' : 'Strategy'
                    }
                    if (t === 'action_item') return 'Action Items'
                    if (t === 'follow_up') return 'Follow Ups'
                    return 'Other'
                  }
                  // sortMode: 'sourceDate_asc' sorts by child occurrence date (parent recurring outstanding list)
                  //           'closedAt_desc' sorts by completion timestamp newest-first (all finished lists)
                  //           null keeps original array order
                  const groupByCategoryWithSort = (list, sortMode) => {
                    const order = ['Strategy', 'Action Items', 'Follow Ups', 'Other decisions', 'Other']
                    const groups = {}
                    list.forEach((o) => {
                      const label = categoryLabel(o)
                      if (!groups[label]) groups[label] = []
                      groups[label].push(o)
                    })
                    return order.filter((label) => groups[label]?.length).map((label) => {
                      let items = [...groups[label]]
                      if (sortMode === 'closedAt_desc') {
                        items.sort((a, b) => {
                          const ta = closedOutputIds.get(a.id) ?? 0
                          const tb = closedOutputIds.get(b.id) ?? 0
                          return tb - ta
                        })
                      } else if (sortMode === 'sourceDate_asc') {
                        items.sort((a, b) => {
                          const ta = a.sourceDate ? new Date(a.sourceDate).getTime() : 0
                          const tb = b.sourceDate ? new Date(b.sourceDate).getTime() : 0
                          return ta - tb
                        })
                      }
                      return { label, items }
                    })
                  }
                  const groupByCategory = (list) => groupByCategoryWithSort(list, isParent ? 'sourceDate_asc' : null)
                  const groupByCategoryFinished = (list) => groupByCategoryWithSort(list, 'closedAt_desc')
                  const saveEdit = async () => {
                    if (editingOutputId == null) return
                    const output = recapOutputs.find((o) => String(o.id) === String(editingOutputId))
                    if (!output) return
                    const sourceMeetingId = output.sourceMeetingId || meetingId
                    setOutputContentLoading((prev) => ({ ...prev, [editingOutputId]: true }))
                    try {
                      await meetingsAPI.updateOutputContent(sourceMeetingId, editingOutputId, {
                        summary: (editingSummary ?? output.summary ?? '').trim() || output.summary,
                        owner: (editingOwner ?? '').trim() || null,
                        sourceMeetingId,
                      })
                      const next = recapOutputs.map((o) =>
                        String(o.id) === String(editingOutputId)
                          ? {
                              ...o,
                              summary: (editingSummary ?? o.summary ?? '').trim() || o.summary,
                              owner: (editingOwner ?? '').trim() || undefined,
                            }
                          : o
                      )
                      setRecapOutputs(next)
                      setEditingOutputId(null)
                      setEditingSummary('')
                      setEditingOwner('')
                      toast.info('Updated', 'Output and assignee saved.')
                    } catch (err) {
                      const msg = err instanceof Error ? err.message : String(err ?? '')
                      toast.error('Could not update output', msg || 'Check that you are the meeting organizer and recap data is available.')
                    } finally {
                      setOutputContentLoading((prev) => ({ ...prev, [editingOutputId]: false }))
                    }
                  }
                  const cancelEdit = () => {
                    setEditingOutputId(null)
                    setEditingSummary('')
                    setEditingOwner('')
                  }
                  const renderOutputRow = (o, isFinished) => {
                    const isEditing = editingOutputId === o.id
                    const canEditThisOutput = isMeetingOrganizer && !isFinished
                    const completionLocked = isFinished
                    const checkboxDisabled =
                      outputStatusLoading[o.id] || completionLocked
                    const baseRowClass = isFinished
                      ? 'flex items-center gap-3 py-2 px-3 rounded-lg bg-primary-50/60 border border-primary-100'
                      : 'flex items-center gap-3 py-2 px-3 rounded-lg bg-gray-50 border border-gray-100 hover:border-gray-200 transition-colors'
                  return (
                      <li key={o.id} className={baseRowClass}>
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={isFinished}
                          onClick={() => !checkboxDisabled && toggleOutput(o, !isFinished)}
                          disabled={checkboxDisabled}
                          title={
                            completionLocked
                              ? 'Completed outputs cannot be changed'
                              : undefined
                          }
                          className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${
                            isFinished ? 'border-primary-500 bg-primary-500' : 'border-gray-300 bg-white hover:border-primary-400'
                          }`}
                        >
                          {outputStatusLoading[o.id] ? (
                            <Loader2 className={`w-3 h-3 animate-spin ${isFinished ? 'text-white' : 'text-primary-500'}`} />
                          ) : isFinished ? (
                            <Check className="w-3 h-3 text-white stroke-[3]" />
                          ) : null}
                        </button>
                        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 md:gap-4 items-center">
                          {isEditing ? (
                            <div className="flex flex-wrap items-center gap-2 md:gap-4 flex-1 min-w-0">
                              <input
                                type="text"
                                value={editingSummary}
                                onChange={(e) => setEditingSummary(e.target.value)}
                                className="input-field flex-1 min-w-[200px]"
                                placeholder="Output"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit()
                                  if (e.key === 'Escape') cancelEdit()
                                }}
                              />
                              <input
                                type="text"
                                value={editingOwner}
                                onChange={(e) => setEditingOwner(e.target.value)}
                                className="input-field w-32 md:w-40"
                                placeholder="Assignee"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit()
                                  if (e.key === 'Escape') cancelEdit()
                                }}
                              />
                              <button
                                type="button"
                                onClick={saveEdit}
                                disabled={outputContentLoading[o.id]}
                                className="btn-primary text-sm py-1.5 px-2"
                              >
                                {outputContentLoading[o.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                              </button>
                              <button type="button" onClick={cancelEdit} className="btn-ghost text-sm py-1.5 px-2">
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <p className={`text-sm font-medium min-w-0 ${isFinished ? 'text-gray-600' : 'text-gray-900'}`}>
                              <span className={isFinished ? 'line-through' : undefined}>
                                {decodeHtmlEntities(o.summary ?? '')}
                                {isParent && o.sourceDate && (
                                  <span className="ml-1.5 text-xs font-normal text-gray-400 whitespace-nowrap">
                                    ({format(new Date(o.sourceDate), 'MMM d')})
                                  </span>
                                )}
                              </span>
                              {isFinished && (() => {
                                const closedAtMs = closedOutputIds.get(o.id)
                                if (!closedAtMs) return null
                                try {
                                  return (
                                    <span className="ml-1.5 text-xs font-normal text-gray-400 whitespace-nowrap">
                                      (completed {format(new Date(closedAtMs), 'MMM d, h:mm a')})
                                    </span>
                                  )
                                } catch {
                                  return null
                                }
                              })()}
                            </p>
                                  )}
                          {!isEditing && (
                            <>
                              <span className="text-sm text-gray-600 md:text-right">{decodeHtmlEntities(formatAssigneeForDisplay(o.owner)) || '—'}</span>
                              {canEditThisOutput ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingOutputId(o.id)
                                    setEditingSummary(o.summary ?? '')
                                    setEditingOwner(formatAssigneeForDisplay(o.owner) ?? '')
                                  }}
                                  className="p-1.5 rounded-lg text-gray-500 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                                  title="Edit output and assignee"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                              ) : (
                                <span
                                  className="w-8 inline-flex justify-end"
                                  title={
                                    !isMeetingOrganizer
                                      ? 'Only the meeting organizer can edit outputs'
                                      : 'Completed outputs cannot be edited'
                                  }
                                >
                                  <Edit2 className="w-4 h-4 text-gray-200 cursor-not-allowed" aria-hidden />
                                </span>
                              )}
                            </>
                          )}
                                </div>
                              </li>
                    )
                  }
                  // Group first, then apply expand logic at the category level.
                  // Initial view: show all items of the first 2 categories fully,
                  // hide subsequent categories entirely behind "See more".
                  // This way complete sections are always shown — never a truncated mid-section.
                  const renderCategoryList = (allItems, isFinishedList, expandCount, setExpandCount) => {
                    const groups = isFinishedList
                      ? groupByCategoryFinished(allItems)
                      : groupByCategory(allItems)
                    if (groups.length === 0) return null
                    const visibleGroups = groups.slice(0, expandCount)
                    const hiddenGroups = groups.slice(expandCount)
                    const hiddenCount = hiddenGroups.reduce((sum, g) => sum + g.items.length, 0)
                    return (
                      <>
                        {visibleGroups.map(({ label, items }) => (
                          <div key={label}>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 mt-3 first:mt-0">{label}</p>
                            <ul className="space-y-1">
                              {items.map((o) => renderOutputRow(o, isFinishedList))}
                            </ul>
                          </div>
                        ))}
                        {hiddenGroups.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setExpandCount((c) => c + 2)}
                            className="mt-2 flex items-center gap-1 text-sm text-primary-600 hover:underline"
                          >
                            <ChevronDown className="w-4 h-4" />
                            See more ({hiddenCount} more in {hiddenGroups.map((g) => g.label).join(', ')})
                          </button>
                        )}
                      </>
                    )
                  }
                  return (
                    <div className="space-y-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Outstanding</h4>
                        {outstanding.length === 0 ? (
                          <p className="text-gray-500 text-sm">No outstanding outputs.</p>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto_auto] gap-2 md:gap-4 px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100 mb-1">
                              <span className="w-5" aria-hidden="true" />
                              <span>Output</span>
                              <span className="md:text-right">Assignee</span>
                              <span className="w-8" aria-hidden="true" />
                            </div>
                            {renderCategoryList(outstanding, false, outstandingExpandCount, setOutstandingExpandCount)}
                          </div>
                        )}
                      </div>
                      {showFinishedSection && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Finished</h4>
                        {finished.length === 0 ? (
                          <p className="text-gray-500 text-sm">No completed outputs yet.</p>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto_auto] gap-2 md:gap-4 px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100 mb-1">
                              <span className="w-5" aria-hidden="true" />
                              <span>Output</span>
                              <span className="md:text-right">Assignee</span>
                              <span className="w-8" aria-hidden="true" />
                            </div>
                            {renderCategoryList(finished, true, finishedExpandCount, setFinishedExpandCount)}
                          </div>
                        )}
                      </div>
                      )}
                    </div>
                  )
                })()}
              </div>
            </div>
            </div>
          )}

          {activeTab === 'agenda' && (
            <div className="space-y-6">
            {/* Explainer banner for joined external Recall meetings (Teams +
                Meet). Without this, the empty agenda confuses users who
                didn't author one upfront — we tell them the agenda will be
                extracted post-meeting from the transcript. Renders before
                the rest of the agenda content. */}
            {isExternalMeeting && isAssistantExternal && (!agendaStatus?.items?.length || agendaStatus.items.length === 0) && (
              <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 flex items-start gap-3">
                <Info className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-violet-900">No agenda yet — this is a joined meeting</p>
                  <p className="text-xs text-violet-800 mt-0.5 leading-relaxed">
                    Rithmo joined this {isMeetMeeting ? 'Google Meet' : 'Microsoft Teams meeting'} as a silent observer. After the meeting ends, Layer 2 will extract decisions, action items, and topic structure from the transcript — no agenda authoring required.
                  </p>
                </div>
              </div>
            )}
            {/* Top row: Prep Notes (the most important thing to review before the
                meeting) sits on the left, next to the Agenda widget on the right.
                The Agenda widget now folds in the old Summary (schedule + people)
                and the meeting documents, so each is half-width. */}
            <div className="grid gap-6 md:grid-cols-2 md:items-start">
            <div className="min-w-0">
              {renderParticipantPrepProfilesCard()}
            </div>
            <div className="min-w-0">
            <div className="glass rounded-2xl p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary-600" />
                  Agenda
                </h3>
                <div className="flex items-center gap-2">
                  <DocumentIntelligencePanel
                    variant="inline-button"
                    meetingId={meetingId}
                    listScope={isChild ? 'effective' : isParent ? 'series' : 'effective'}
                    title={null}
                    showAgendaSuggestions={false}
                    onAttachmentsMutated={bumpAgendaDocuments}
                    onAfterApply={async () => {
                      await fetchMeetingData()
                      if (isLive) await fetchAgendaStatus()
                    }}
                  />
                  {canEditAgenda && !isLive && !isEditingAgenda && (
                    <button
                      type="button"
                      onClick={() => setIsEditingAgenda(true)}
                      className="text-sm font-normal text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                </div>
              </div>
              {(meeting?.audienceMode ?? 'internal') === 'external' && (
                <div className="mb-4 space-y-2">
                  <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                    <button
                      type="button"
                      onClick={() => setAgendaLens('public')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        agendaLens === 'public' ? 'bg-white shadow text-primary-700' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Public agenda
                    </button>
                    <button
                      type="button"
                      onClick={() => setAgendaLens('internal')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        agendaLens === 'internal' ? 'bg-white shadow text-primary-700' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Internal goals (private)
                    </button>
                  </div>
                  <p className="text-sm text-gray-500">
                    {agendaLens === 'public'
                      ? 'Rithmo follows this public agenda when speaking in the room. Guests see these topics.'
                      : 'Private to your team — not shown on the guest-facing agenda.'}
                  </p>
                </div>
              )}
              {(meeting?.audienceMode ?? 'internal') === 'external' && agendaLens === 'internal' ? (
                <InternalAgendaSummaryPanel
                  meetingId={meetingId}
                  publicAgendaItems={agendaItemsForPublicAgendaTab}
                />
              ) : (
              <>
              {/* Phase 3 Agenda Composer V2 panel — DISABLED to avoid duplicate rendering.
                  The composer auto-generates items straight into meeting_agenda_items
                  (source='learning_engine'), so they already appear in the main agenda
                  list below in the original style with an "AI suggested" badge. Rendering
                  this panel as well showed the same items twice in two different styles.
                  Backend generation + the AgendaComposerV2Panel component are left intact;
                  re-enable this block to bring back the rich composer surface. */}
              {/* <div className="mb-4">
                <AgendaComposerV2Panel meetingId={meetingId} />
              </div> */}
              {/* Phase 4 Exec Pre-Read panel — renders for organizers on parent + one-off meetings */}
              <div className="mb-4">
                <ExecPreReadPanel meetingId={meetingId} meetingTitle={meeting?.title} />
              </div>
              {meeting?.llmAgendaAppliedAt && meeting?.hasLlmAgendaSnapshot && (
                <div className="mb-4 flex items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3">
                  <Sparkles className="w-5 h-5 text-violet-600 shrink-0" />
                  <p className="text-sm text-violet-800 flex-1">
                    Agenda updated based on your last meeting{' '}
                    <span className="text-violet-500">
                      ({(() => {
                        try {
                          const d = new Date(meeting.llmAgendaAppliedAt)
                          const now = new Date()
                          const diffMs = now - d
                          const diffMin = Math.floor(diffMs / 60000)
                          if (diffMin < 60) return `${diffMin}m ago`
                          const diffHrs = Math.floor(diffMin / 60)
                          if (diffHrs < 24) return `${diffHrs}h ago`
                          const diffDays = Math.floor(diffHrs / 24)
                          return `${diffDays}d ago`
                        } catch { return '' }
                      })()})
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await agendaAPI.revertLlmAgenda(meetingId)
                        await fetchMeetingData()
                        toast.success('Agenda reverted to original')
                      } catch (err) {
                        toast.error('Failed to revert agenda')
                      }
                    }}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-50 transition-colors"
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                    Revert to original
                  </button>
                </div>
              )}
              {canEditAgenda && !isLive && isEditingAgenda ? (
                <>
                  {editableAgenda.length > 0 ? (
                    <Reorder.Group
                      axis="y"
                      values={editableAgenda}
                      onReorder={setEditableAgenda}
                      className="space-y-3"
                    >
                      {editableAgenda.map((item, index) => (
                        <Reorder.Item
                          key={item.id ?? index}
                          value={item}
                          className="flex flex-col gap-3 p-4 rounded-xl border bg-gray-50 border-gray-100 cursor-default"
                        >
                          <div className="flex items-start gap-3">
                            <div className="shrink-0 flex flex-col items-center gap-1 pt-1">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-medium bg-gradient-primary text-white">
                                {index + 1}
                              </div>
                              <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none">
                                <GripVertical className="w-4 h-4" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0 space-y-2">
                              <input
                                type="text"
                                value={item.title}
                                onChange={(e) => setEditableAgenda((prev) => {
                                  const next = [...prev]
                                  next[index] = { ...next[index], title: e.target.value }
                                  return next
                                })}
                                placeholder="Agenda item title"
                                className="input-field w-full font-medium"
                              />
                              <textarea
                                value={item.description}
                                onChange={(e) => setEditableAgenda((prev) => {
                                  const next = [...prev]
                                  next[index] = { ...next[index], description: e.target.value }
                                  return next
                                })}
                                placeholder="Description (optional)"
                                rows={2}
                                className="input-field w-full text-sm"
                              />
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-500 shrink-0" />
                                <input
                                  type="number"
                                  min={1}
                                  max={120}
                                  value={Math.round((item.durationSec ?? 300) / 60)}
                                  onChange={(e) => {
                                    const min = parseInt(e.target.value, 10)
                                    if (!Number.isNaN(min) && min >= 1) {
                                      setEditableAgenda((prev) => {
                                        const next = [...prev]
                                        next[index] = { ...next[index], durationSec: min * 60 }
                                        return next
                                      })
                                    }
                                  }}
                                  className="input-field w-20 text-sm"
                                />
                                <span className="text-sm text-gray-500">min</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setEditableAgenda((prev) => prev.filter((_, i) => i !== index))}
                              className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </Reorder.Item>
                      ))}
                    </Reorder.Group>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No agenda items</p>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditableAgenda((prev) => [
                      ...prev,
                      { id: `new-${Date.now()}`, title: '', description: '', durationSec: 300 }
                    ])}
                    className="mt-4 btn-ghost flex items-center gap-2 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add agenda item
                  </button>
                  <div className="flex flex-wrap gap-2 mt-4">
                      <button
                        onClick={handleCancelAgendaEdit}
                        disabled={agendaSaving}
                        className="btn-ghost flex items-center gap-2"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveAgenda}
                        disabled={agendaSaving}
                        className="btn-secondary flex items-center gap-2"
                      >
                        {agendaSaving ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Save className="w-5 h-5" />
                        )}
                        {agendaSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleSaveAndSendUpdate}
                        disabled={agendaSaving}
                        className="btn-primary flex items-center gap-2"
                      >
                        {agendaSaving ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                        {agendaSaving ? 'Sending...' : 'Save and send update to participants'}
                      </button>
                    </div>
                </>
              ) : (
                <>
                  {agendaItemsForPublicAgendaTab.length > 0 ? (
                    <div className="space-y-3">
                      {agendaItemsForPublicAgendaTab.map((item, index) => (
                        <div
                          key={item.id ?? index}
                          className={`flex items-center gap-4 p-4 rounded-xl border ${
                            isLive && (agendaStatus?.currentIndex ?? -1) === index
                              ? 'bg-primary-50 border-primary-200'
                              : 'bg-gray-50 border-gray-100'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium ${
                            isLive && (agendaStatus?.currentIndex ?? -1) === index
                              ? 'bg-gradient-primary text-white'
                              : isLive && (agendaStatus?.currentIndex ?? -1) > index
                              ? 'bg-green-500 text-white'
                              : 'bg-gradient-primary text-white'
                          }`}>
                            {isLive && (agendaStatus?.currentIndex ?? -1) > index ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              index + 1
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-gray-900">{decodeHtmlEntities(item.title ?? item.name)}</p>
                              {item.source === 'proxy_brief' ? (
                                <span
                                  className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 border border-amber-200"
                                  title="Generated from your leader proxy brief"
                                >
                                  From brief
                                </span>
                              ) : item.source === 'learning_engine' ? (
                                <span
                                  className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700 border border-violet-200"
                                  title="AI-suggested based on your previous meetings"
                                >
                                  AI suggested
                                </span>
                              ) : null}
                              {item.isPinned ? (
                                <span
                                  className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 border border-blue-200"
                                  title="Pinned — survives automated rebuilds"
                                >
                                  Pinned
                                </span>
                              ) : null}
                            </div>
                            <AgendaItemDescription text={item.description ?? item.details} />
                            {item.rationale ? (
                              <p className="text-xs text-gray-500 mt-1 italic">{decodeHtmlEntities(item.rationale)}</p>
                            ) : null}
                            {isLive && (agendaStatus?.currentIndex ?? -1) === index && (
                              <p className="text-sm text-primary-600 mt-1">Currently discussing</p>
                            )}
                            {isMeetingOrganizer && (item.source === 'learning_engine' || item.source === 'human') ? (
                              <div className="flex items-center gap-3 mt-2 text-[11px]">
                                {item.source === 'learning_engine' ? (
                                  <button
                                    type="button"
                                    className="text-violet-700 hover:text-violet-900 underline-offset-2 hover:underline"
                                    onClick={async (e) => {
                                      e.preventDefault()
                                      if (!window.confirm('Reject this suggestion? The engine will skip it for the next 4 cycles.')) return
                                      try {
                                        const res = await fetch(`/api/meetings/${meetingId}/agenda/items/${item.id ?? item.agendaItemId}/reject`, {
                                          method: 'POST',
                                          credentials: 'include',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ scope: 'suppress', cycles: 4 }),
                                        })
                                        if (!res.ok) {
                                          const errPayload = await res.json().catch(() => ({}))
                                          window.alert(`Reject failed: ${errPayload?.error ?? res.statusText}`)
                                          return
                                        }
                                        window.location.reload()
                                      } catch (err) {
                                        window.alert(`Reject failed: ${err?.message ?? 'network error'}`)
                                      }
                                    }}
                                  >
                                    Reject
                                  </button>
                                ) : null}
                                {item.source === 'human' || item.source === 'learning_engine' ? (
                                  <button
                                    type="button"
                                    className="text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline"
                                    onClick={async (e) => {
                                      e.preventDefault()
                                      const verb = item.isPinned ? 'unpin' : 'pin'
                                      try {
                                        const res = await fetch(`/api/meetings/${meetingId}/agenda/items/${item.id ?? item.agendaItemId}/${verb}`, {
                                          method: 'POST',
                                          credentials: 'include',
                                          headers: { 'Content-Type': 'application/json' },
                                        })
                                        if (!res.ok) {
                                          const errPayload = await res.json().catch(() => ({}))
                                          window.alert(`${verb} failed: ${errPayload?.error ?? res.statusText}`)
                                          return
                                        }
                                        window.location.reload()
                                      } catch (err) {
                                        window.alert(`${verb} failed: ${err?.message ?? 'network error'}`)
                                      }
                                    }}
                                  >
                                    {item.isPinned ? 'Unpin' : 'Pin'}
                                  </button>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                          <div className="flex shrink-0 items-center gap-2 text-gray-500 tabular-nums whitespace-nowrap">
                            <Clock className="w-4 h-4 shrink-0" />
                            <span>{item.duration ?? Math.round((item.durationSec ?? 300) / 60)} min</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No agenda items</p>
                  )}
                </>
              )}
              </>
              )}
              {/* Meeting documents widget disabled: the screenshare sharing
                  feature is not ready for primetime. Uploads still work via the
                  button in the Agenda widget header (used for agenda generation),
                  but the document list / sharing settings UI is hidden.
              <AgendaTabDocumentsSection
                embedded
                meetingId={meetingId}
                listScope={isChild ? 'effective' : isParent ? 'series' : 'effective'}
                refreshKey={agendaDocumentsVersion}
                readOnly={!isMeetingOrganizer}
                allowDelete={isMeetingOrganizer}
                onMutated={bumpAgendaDocuments}
              />
              */}
            </div>
            </div>
            </div>
              {/* External participant enrichment: bios + recent news. Renders
                  nothing when the feature is off or there are no externals. */}
              <div className="mt-6">
                <ParticipantEnrichment meetingId={meetingId} />
              </div>
              {isMeetingOrganizer ? (
                <div className="min-w-0">{renderPersonalInsightsCard()}</div>
              ) : null}
              {/* Meeting brief widget disabled (deemed not useful).
              {isMeetingOrganizer && <BriefReviewPanel meetingId={meetingId} />}
              */}
              {isMeetingOrganizer && (
                <div className="glass rounded-2xl p-6 mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Pre-Meeting Insights</h3>
                  <PreMeetingInsightsPanel meetingId={meetingId} />
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && isParent && (
            <div className="glass rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Repeat className="w-5 h-5 text-primary-600" />
                Meeting history
              </h3>
              <p className="text-sm text-gray-500 mb-6">Past and upcoming instances of this recurring meeting.</p>
              {seriesInstancesLoading ? (
                <p className="text-gray-500">Loading history…</p>
              ) : seriesInstances.length === 0 ? (
                <p className="text-gray-500">No past or upcoming instances yet.</p>
              ) : (
                <div className="space-y-4">
                  {seriesInstances.map((inst) => {
                    const instMeetingId = inst.meetingId ?? inst.id
                    const attendedParticipants = inst.participants ?? []
                    const displayName = (p) =>
                      decodeHtmlEntities(p.displayName ?? p.name ?? p.email ?? '') || '—'
                    return (
                    <Link
                      key={instMeetingId}
                      to={`/meeting/${instMeetingId}`}
                      className="block p-4 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors cursor-pointer"
                    >
                      <div className="flex flex-wrap items-center gap-6">
                        <div>
                          <p className="text-sm text-gray-400">Date</p>
                          <p className="font-medium text-gray-900">
                            {inst.startedAtMs ? format(new Date(inst.startedAtMs), 'EEEE, MMM d, yyyy') : inst.scheduledStartTime ? format(parseISO(inst.scheduledStartTime), 'EEEE, MMM d, yyyy') : inst.startTime ? format(parseISO(inst.startTime), 'EEEE, MMM d, yyyy') : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Start</p>
                          <p className="font-medium text-gray-900">
                            {inst.startedAtMs ? fmtTime(inst.startedAtMs) : inst.scheduledStartTime ? fmtTime(inst.scheduledStartTime) : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">End</p>
                          <p className="font-medium text-gray-900">
                            {inst.endedAtMs ? fmtTime(inst.endedAtMs) : inst.scheduledEndTime ? fmtTime(inst.scheduledEndTime) : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400">Participants</p>
                          {attendedParticipants.length > 0 ? (
                            <p className="font-medium text-gray-900 text-sm mt-1">
                              {attendedParticipants.map((p, i) => displayName(p) || `Participant ${i + 1}`).join(', ')}
                            </p>
                          ) : (
                            <p className="font-medium text-gray-900">{inst.participantCount ?? 0} people</p>
                          )}
                        </div>
                        <div>
                          <span className={`badge ${inst.status === 'ended' ? 'badge-success' : 'badge-accent'}`}>
                            {inst.status ?? '—'}
                          </span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
                      </div>
                    </Link>
                    )
                  })}
                          </div>
                  )}
                </div>
          )}

          {activeTab === 'followups' && (
            <FollowupEmailDrafts meetingId={meetingId} />
          )}

        </motion.div>
      </main>
    </div>
  )
}

export default MeetingDetailPage
